import { supabase } from './supabase';

// Generic helper for error handling
const handleSupabaseError = (error, defaultMessage = 'An error occurred') => {
  console.error('Supabase error:', error);
  return {
    error: error?.message || defaultMessage,
    data: null
  };
};

/* -------------------------------------------------------------------------- */
/*                              BRANCHES                                      */
/* -------------------------------------------------------------------------- */

// Get all branches for an organization
export const getBranches = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('branches')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single branch by ID
export const getBranch = async id => {
  try {
    const { data, error } = await supabase
      .from('branches')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create branch
export const createBranch = async branchData => {
  try {
    const { data, error } = await supabase
      .from('branches')
      .insert([branchData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update branch
export const updateBranch = async (id, branchData) => {
  try {
    const { data, error } = await supabase
      .from('branches')
      .update(branchData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete branch
export const deleteBranch = async id => {
  try {
    // First, get the branch to retrieve the logo URL
    const { data: branch, error: fetchError } = await supabase
      .from('branches')
      .select('logo')
      .eq('id', id)
      .single();

    if (fetchError) return handleSupabaseError(fetchError);

    // Delete the branch from database
    const { error } = await supabase.from('branches').delete().eq('id', id);

    if (error) return handleSupabaseError(error);

    // Delete the logo from storage if it exists and is a Supabase URL
    if (branch?.logo && branch.logo.includes('supabase')) {
      try {
        const urlParts = branch.logo.split(
          '/storage/v1/object/public/uploads/'
        );
        if (urlParts.length >= 2) {
          const path = urlParts[1];
          await supabase.storage.from('uploads').remove([path]);
          console.log('Branch logo deleted from storage');
        }
      } catch (storageError) {
        console.error('Failed to delete logo from storage:', storageError);
        // Don't fail the whole operation if logo deletion fails
      }
    }

    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              DONORS                                        */
/* -------------------------------------------------------------------------- */

// Lightweight columns for the donors table (no large JSON blobs)
// `address` jsonb is included so the global search can index the postcode
// field directly — primary_address text isn't always reliable.
const DONOR_TABLE_COLUMNS =
  'id, simple_id, first_name, last_name, primary_email, primary_phone, primary_address, address, created_at';

// Fetch all donors with only the columns needed for table display
export const getAllDonorsLight = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    const BATCH_SIZE = 1000;
    let allDonors = [];
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      let donorQuery = supabase
        .from('donars')
        .select(DONOR_TABLE_COLUMNS)
        .eq('organisation_id', organisationId);

      if (branchId) {
        donorQuery = donorQuery.eq('branch_id', branchId);
      }

      if (filters.simpleIds && filters.simpleIds.length > 0) {
        donorQuery = donorQuery.in('simple_id', filters.simpleIds);
      }

      if (filters.startDate && filters.endDate) {
        donorQuery = donorQuery
          .gte('created_at', filters.startDate)
          .lte('created_at', filters.endDate);
      }

      const { data: donors, error: donorError } = await donorQuery
        .order('created_at', { ascending: false })
        .range(from, from + BATCH_SIZE - 1);

      if (donorError) return { data: null, error: donorError.message };

      allDonors = [...allDonors, ...donors];
      hasMore = donors.length === BATCH_SIZE;
      from += BATCH_SIZE;
    }

    // Single aggregated query for ALL order totals (GROUP BY in JS since Supabase doesn't support SQL GROUP BY directly)
    const totalsMap = {};
    let orderFrom = 0;
    let orderHasMore = true;

    while (orderHasMore) {
      let orderQuery = supabase
        .from('orders')
        .select('donar_id, donation_amount')
        .eq('organisation_id', organisationId);

      if (branchId) {
        orderQuery = orderQuery.eq('branch_id', branchId);
      }

      const { data: orders, error: orderError } = await orderQuery.range(
        orderFrom,
        orderFrom + BATCH_SIZE - 1
      );

      if (orderError || !orders) break;

      for (const order of orders) {
        totalsMap[order.donar_id] =
          (totalsMap[order.donar_id] || 0) + (order.donation_amount || 0);
      }

      orderHasMore = orders.length === BATCH_SIZE;
      orderFrom += BATCH_SIZE;
    }

    const donorsWithTotals = allDonors.map(donor => ({
      ...donor,
      total_donation: totalsMap[donor.id] || 0
    }));

    return { data: donorsWithTotals, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Server-side donor search + pagination + per-donor donation totals in ONE
// round-trip via the search_donors RPC. Replaces loading all 7k donors + all
// orders into the browser. `full_count` (returned on every row) is the total
// number of matches across ALL donors, so search is never limited to a page.
export const searchDonors = async (
  organisationId,
  branchId,
  {
    search = '',
    page = 0,
    pageSize = 50,
    startDate = null,
    endDate = null,
    simpleIds = null
  } = {}
) => {
  try {
    const { data, error } = await supabase.rpc('search_donors', {
      p_org: organisationId,
      p_branch: branchId || null,
      p_search: search ? search.trim() : null,
      p_limit: pageSize,
      p_offset: page * pageSize,
      p_start: startDate,
      p_end: endDate,
      p_simple_ids: simpleIds && simpleIds.length ? simpleIds : null
    });

    if (error) return { data: null, error: error.message };

    const donors = data || [];
    const totalCount = donors.length > 0 ? Number(donors[0].full_count) : 0;
    return { data: { donors, totalCount }, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Fetch notes (description only) for many donors at once, batched by donor id.
// Used by the global header search to make note content searchable client-side.
export const getDonorFeedbackByDonorIds = async donorIds => {
  if (!Array.isArray(donorIds) || donorIds.length === 0) {
    return { data: [], error: null };
  }
  try {
    const CHUNK = 200;
    let all = [];
    for (let i = 0; i < donorIds.length; i += CHUNK) {
      const chunk = donorIds.slice(i, i + CHUNK);
      const { data, error } = await supabase
        .from('donars_feedback')
        .select('donar_id, description')
        .in('donar_id', chunk);
      if (error) return handleSupabaseError(error);
      if (data) all = all.concat(data);
    }
    return { data: all, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single donor by ID

// PostgREST filters travel in the URL; ~640 uuids in one in() pushes the
// request past the gateway's URL limit and it fails with 400 (hit 2026-09-16
// when the org passed 640 children). Run large in() lists in batches.
const IN_CHUNK = 150;
export const selectInChunks = async (buildQuery, column, values) => {
  let all = [];
  for (let i = 0; i < values.length; i += IN_CHUNK) {
    const { data, error } = await buildQuery().in(
      column,
      values.slice(i, i + IN_CHUNK)
    );
    if (error) return { data: null, error };
    if (data) all = all.concat(data);
  }
  return { data: all, error: null };
};

export const getDonor = async id => {
  try {
    const { data, error } = await supabase
      .from('donars')
      .select(
        `
        *,
        account_manager:account_manager_id(id, first_name, last_name, email)
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor
export const createDonor = async donorData => {
  try {
    const { data, error } = await supabase
      .from('donars')
      .insert([donorData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor
export const updateDonor = async (id, donorData) => {
  try {
    const { data, error } = await supabase
      .from('donars')
      .update(donorData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor
export const deleteDonor = async id => {
  try {
    const { error } = await supabase.from('donars').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donor notes/feedback count
export const getDonorNotesCount = async donorId => {
  try {
    const { count, error } = await supabase
      .from('donars_feedback')
      .select('*', { count: 'exact', head: true })
      .eq('donar_id', donorId);

    if (error) return handleSupabaseError(error);
    return { data: count, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR INSIGHTS/STATS                             */
/* -------------------------------------------------------------------------- */

// Get donor insights (all stats in one query for efficiency)
export const getDonorInsights = async (donorId, branchId) => {
  try {
    // Get all paid orders for this donor
    let ordersQuery = supabase
      .from('orders')
      .select(
        `
        id,
        donation_amount,
        created_at,
        order_details (
          amount,
          donation_type:donation_type_id (
            donation_type_name
          )
        )
      `
      )
      .eq('donar_id', donorId)
      .eq('payment_status', 'paid')
      .order('created_at', { ascending: true });

    if (branchId) {
      ordersQuery = ordersQuery.eq('branch_id', branchId);
    }

    const { data: orders, error: ordersError } = await ordersQuery;

    if (ordersError) return handleSupabaseError(ordersError);

    // Get donor creation date
    const { data: donor, error: donorError } = await supabase
      .from('donars')
      .select('created_at')
      .eq('id', donorId)
      .single();

    if (donorError) return handleSupabaseError(donorError);

    // Calculate all stats from the orders
    const stats = {
      lifetimeContribution: 0,
      ytdContribution: 0,
      firstDonation: null,
      mostRecentDonation: null,
      averageDonation: 0,
      lastDonationDate: null,
      donorCreatedAt: donor.created_at,
      totalDonations: orders?.length || 0,
      yearlyDonations: {},
      categories: {}
    };

    if (orders && orders.length > 0) {
      const currentYear = new Date().getFullYear();
      const yearStart = new Date(currentYear, 0, 1).toISOString();

      orders.forEach(order => {
        const amount = order.donation_amount || 0;
        const orderYear = new Date(order.created_at).getFullYear();

        // Lifetime contribution
        stats.lifetimeContribution += amount;

        // YTD contribution
        if (order.created_at >= yearStart) {
          stats.ytdContribution += amount;
        }

        // Yearly donations
        if (!stats.yearlyDonations[orderYear]) {
          stats.yearlyDonations[orderYear] = 0;
        }
        stats.yearlyDonations[orderYear] += amount;

        // Categories
        if (order.order_details && order.order_details.length > 0) {
          order.order_details.forEach(detail => {
            const categoryName =
              detail.donation_type?.donation_type_name || 'General Donation';
            const detailAmount = detail.amount || 0;
            stats.categories[categoryName] =
              (stats.categories[categoryName] || 0) + detailAmount;
          });
        } else if (amount > 0) {
          stats.categories['General Donation'] =
            (stats.categories['General Donation'] || 0) + amount;
        }
      });

      // First and most recent donations
      stats.firstDonation = orders[0].donation_amount;
      stats.mostRecentDonation = orders[orders.length - 1].donation_amount;
      stats.lastDonationDate = orders[orders.length - 1].created_at;

      // Average donation
      stats.averageDonation = stats.lifetimeContribution / orders.length;
    }

    return { data: stats, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR ORDERS                                     */
/* -------------------------------------------------------------------------- */

// Get all orders for a specific donor
export const getDonorOrders = async (donorId, branchId) => {
  try {
    let ordersQuery = supabase
      .from('orders')
      .select(
        `
        id,
        simple_id,
        gift_aid_eligible,
        payment_status,
        donation_amount,
        subtotal,
        transaction_fees,
        created_at,
        updated_at,
        branch_id,
        donar_id,
        organisation_id,
        payment_method_id,
        payment_method:payment_method_id (
          payment_name
        ),
        pledge_id,
        pledge:pledge_id (
          id,
          simple_id
        ),
        order_details (
          billing_condition,
          package:package_id (
            package_title
          )
        )
      `
      )
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (branchId) {
      ordersQuery = ordersQuery.eq('branch_id', branchId);
    }

    const { data, error } = await ordersQuery;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update gift aid status for an order
export const updateOrderGiftAid = async (orderId, giftAidEligible) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .update({ gift_aid_eligible: giftAidEligible })
      .eq('id', orderId)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR SPONSORSHIPS                               */
/* -------------------------------------------------------------------------- */

// Get sponsorships by donor ID
export const getDonorSponsorships = async donorId => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .select(
        `
        id,
        stipulation,
        theme,
        duration,
        renewal,
        created_at,
        start_date,
        updated_at,
        donar_id,
        orphan_id,
        status,
        allocated_amount,
        coverage_percentage,
        orphan:orphan_id (
          id,
          simple_id,
          country,
          first_name,
          gender,
          last_name,
          middle_name,
          image,
          DOB
        ),
        beneficiary_sponsorship_type:beneficiary_sponsorship_type_id (
          sponsorship_type:sponsorship_type_id (
            type
          )
        )
      `
      )
      // All statuses on purpose: the donor Sponsorships tab shows active
      // sponsorships plus an ended/cancelled history section.
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR GIFT AID                                   */
/* -------------------------------------------------------------------------- */

// Get gift aid data for a donor (combines all gift aid queries)
export const getDonorGiftAid = async donorId => {
  try {
    // Get gift aid record
    const { data: giftAid, error: giftAidError } = await supabase
      .from('gift_aid')
      .select('id, amount')
      .eq('donar_id', donorId)
      .maybeSingle();

    if (giftAidError) return handleSupabaseError(giftAidError);

    // Get eligibility from most recent order
    const { data: orders, error: ordersError } = await supabase
      .from('orders')
      .select('gift_aid_eligible')
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (ordersError) return handleSupabaseError(ordersError);

    // Get declaration log if gift aid exists
    let declarationLog = [];
    if (giftAid?.id) {
      const { data: logData, error: logError } = await supabase
        .from('declaration_log')
        .select('*')
        .eq('gift_aid_id', giftAid.id)
        .order('created_at', { ascending: false });

      if (logError) return handleSupabaseError(logError);
      declarationLog = logData || [];
    }

    return {
      data: {
        giftAidId: giftAid?.id || null,
        awaitingSubmission: giftAid?.amount || 0,
        eligible: orders?.[0]?.gift_aid_eligible || false,
        declarationLog
      },
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                            DONOR DOCUMENTS                                 */
/* -------------------------------------------------------------------------- */

const DONOR_DOC_SELECT = `
  id,
  donor_id,
  document_type,
  notes,
  file_url,
  file_name,
  file_size,
  created_at,
  uploaded_by_user:uploaded_by (
    first_name,
    last_name
  )
`;

export const getDonorDocuments = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donor_supporting_documents')
      .select(DONOR_DOC_SELECT)
      .eq('donor_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const createDonorDocument = async docData => {
  try {
    const { data, error } = await supabase
      .from('donor_supporting_documents')
      .insert([docData])
      .select(DONOR_DOC_SELECT)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteDonorDocument = async id => {
  try {
    const { error } = await supabase
      .from('donor_supporting_documents')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR FEEDBACK/NOTES                             */
/* -------------------------------------------------------------------------- */

// Get all feedback/notes for a donor
export const getDonorFeedback = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donars_feedback')
      .select(
        `
        id,
        description,
        donar_id,
        priority,
        note_type,
        is_pinned,
        created_at,
        user:user_id (
          first_name,
          last_name
        )
      `
      )
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor feedback/note
export const createDonorFeedback = async feedbackData => {
  try {
    const { data, error } = await supabase
      .from('donars_feedback')
      .insert([feedbackData])
      .select(
        `
        id,
        description,
        donar_id,
        priority,
        note_type,
        is_pinned,
        created_at,
        user:user_id (
          first_name,
          last_name
        )
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor feedback/note
export const updateDonorFeedback = async (id, feedbackData) => {
  try {
    const { data, error } = await supabase
      .from('donars_feedback')
      .update(feedbackData)
      .eq('id', id)
      .select(
        `
        id,
        description,
        donar_id,
        priority,
        note_type,
        is_pinned,
        created_at,
        user:user_id (
          first_name,
          last_name
        )
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor feedback/note
export const deleteDonorFeedback = async id => {
  try {
    const { error } = await supabase
      .from('donars_feedback')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donors with donation totals for date range (for stats)
export const getDonorsWithOrdersByDate = async (
  organisationId,
  branchId,
  startDate,
  endDate
) => {
  try {
    console.log('getDonorsWithOrdersByDate called with:', {
      organisationId,
      branchId,
      startDate,
      endDate,
      branchIdType: typeof branchId
    });

    // Get donor count - simplified query
    let donorQuery = supabase
      .from('donars')
      .select('id', { count: 'exact' })
      .eq('organisation_id', organisationId);

    if (branchId) {
      donorQuery = donorQuery.eq('branch_id', branchId);
    }

    if (startDate && endDate) {
      donorQuery = donorQuery
        .gte('created_at', startDate)
        .lte('created_at', endDate);
    }

    const { count: donorCount, error: donorError } = await donorQuery;

    if (donorError) {
      console.error('Donor count query error:', {
        error: donorError,
        errorKeys: Object.keys(donorError),
        errorJSON: JSON.stringify(donorError),
        organisationId,
        branchId,
        startDate,
        endDate
      });
      return handleSupabaseError(donorError);
    }

    // Get orders for the date range
    let orderQuery = supabase
      .from('orders')
      .select('id, donation_amount, donar_id')
      .eq('organisation_id', organisationId);

    if (branchId) {
      orderQuery = orderQuery.eq('branch_id', branchId);
    }

    if (startDate && endDate) {
      orderQuery = orderQuery
        .gte('created_at', startDate)
        .lte('created_at', endDate);
    }

    const { data: orders, error: orderError } = await orderQuery;

    if (orderError) {
      console.error('Orders query error:', orderError);
      // Don't fail the whole query if orders fail, just return empty orders
      return {
        data: {
          donorCount: donorCount || 0,
          orders: []
        },
        error: null
      };
    }

    return {
      data: {
        donorCount: donorCount || 0,
        orders: orders || []
      },
      error: null
    };
  } catch (error) {
    console.error('getDonorsWithOrdersByDate error:', error);
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR CATEGORIES                                 */
/* -------------------------------------------------------------------------- */

// Get all donor categories (shared across all organizations/branches)
export const getDonorCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('donor_category')
      .select('*')
      .order('title', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor category
export const createDonorCategory = async ({ title, isDefault }) => {
  try {
    const { data, error } = await supabase
      .from('donor_category')
      .insert([
        {
          title,
          is_default: isDefault
        }
      ])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor category
export const updateDonorCategory = async (id, { title, isDefault }) => {
  try {
    const { data, error } = await supabase
      .from('donor_category')
      .update({ title, is_default: isDefault })
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor category
export const deleteDonorCategory = async id => {
  try {
    const { error } = await supabase
      .from('donor_category')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */

// Get all currencies
export const getCurrencies = async () => {
  try {
    const { data, error } = await supabase
      .from('currency')
      .select('*')
      .order('currency_name', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              LOCATIONS                                     */
/* -------------------------------------------------------------------------- */

// Get locations by organisation
export const getLocations = async (organisationId, branchId, type) => {
  try {
    let query = supabase
      .from('location')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query.order('location_name', {
      ascending: true
    });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                            ORGANISATION                                    */
/* -------------------------------------------------------------------------- */

// Columns the browser is allowed to read. stripe_secret is deliberately absent:
// SELECT on that column is revoked from authenticated, so `*` would 403.
const ORGANISATION_COLUMNS =
  'id, organisation_name, charity_number, city, post_code, country, ' +
  'organisation_email, logo, created_at, updated_at, currency_id, ' +
  'parent_id, address_1, address_2, stripe_key';

// Get organisation by ID
export const getOrganisation = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('organisation')
      .select(ORGANISATION_COLUMNS)
      .eq('id', organisationId)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update organisation
export const updateOrganisation = async (id, organisationData) => {
  try {
    const { data, error } = await supabase
      .from('organisation')
      .update(organisationData)
      .eq('id', id)
      .select(ORGANISATION_COLUMNS)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           BRANCH PAYMENT SETTINGS                          */
/* -------------------------------------------------------------------------- */

// Get payment settings for a branch
export const getBranchPaymentSettings = async branchId => {
  try {
    const { data, error } = await supabase
      .from('payment')
      .select('id, branch_id, stripe_key')
      .eq('branch_id', branchId)
      .single();

    if (error && error.code !== 'PGRST116') return handleSupabaseError(error);
    return { data: data || null, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create or update payment settings for a branch
export const upsertBranchPaymentSettings = async (branchId, paymentData) => {
  try {
    // First check if settings exist for this branch
    const { data: existing } = await supabase
      .from('payment')
      .select('id')
      .eq('branch_id', branchId)
      .single();

    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('payment')
        .update({
          stripe_key: paymentData.stripe_key,
          stripe_secret: paymentData.stripe_secret
        })
        .eq('branch_id', branchId)
        // Writing the secret is allowed; reading it back is not.
        .select('id, branch_id, stripe_key, payment_type')
        .single();

      if (error) return handleSupabaseError(error);
      return { data, error: null };
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('payment')
        .insert({
          branch_id: branchId,
          stripe_key: paymentData.stripe_key,
          stripe_secret: paymentData.stripe_secret,
          payment_type: 'stripe'
        })
        .select('id, branch_id, stripe_key, payment_type')
        .single();

      if (error) return handleSupabaseError(error);
      return { data, error: null };
    }
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              USERS & ROLES                                 */
/*  -------------------------------------------------------------------------- */

// Get user roles for organization (org admin view)
export const getUserRoles = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        *,
        user:user_id (
          id,
          first_name,
          last_name,
          email,
          email_verified_at
        ),
        role:role_id (
          id,
          name
        ),
        branch:branch_id (
          id,
          name
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get user roles for specific branch (branch admin view)
export const getBranchUserRoles = async (organisationId, branchId) => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        *,
        user:user_id (
          id,
          first_name,
          last_name,
          email,
          email_verified_at
        ),
        role:role_id (
          id,
          name
        ),
        branch:branch_id (
          id,
          name
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('branch_id', branchId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single user role by ID
export const getUserRole = async id => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        *,
        user:user_id (
          id,
          first_name,
          last_name,
          email,
          email_verified_at
        ),
        role:role_id (
          id,
          name
        ),
        branch:branch_id (
          id,
          name
        )
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Check whether a user already exists by email within an organisation.
// Used by the add-user form to skip the password step when assigning an
// existing user to another branch (a person has one login across branches).
export const checkUserExistsByEmail = async (email, organisationId) => {
  try {
    if (!email || !organisationId) {
      return { data: null, error: null };
    }
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, email')
      .eq('email', email)
      .eq('organisation_id', organisationId)
      .maybeSingle();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create user and user role via Edge Function
export const createUser = async ({
  email,
  password,
  firstName,
  lastName,
  organisationId,
  branchId,
  roleId,
  permissions
}) => {
  try {
    // Get the current session token
    const {
      data: { session }
    } = await supabase.auth.getSession();

    if (!session) {
      return handleSupabaseError({ message: 'Not authenticated' });
    }

    // Call the Edge Function to create user with admin privileges
    const { data, error } = await supabase.functions.invoke('create-user', {
      body: {
        email,
        password,
        firstName,
        lastName,
        organisationId,
        branchId,
        roleId,
        permissions
      }
    });

    if (error) return handleSupabaseError(error);
    if (data?.error) return handleSupabaseError({ message: data.error });

    return { data: data.data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update user role
export const updateUserRole = async (id, userRoleData) => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .update(userRoleData)
      .eq('id', id)
      .select(
        `
        *,
        user:user_id (
          id,
          first_name,
          last_name,
          email,
          email_verified_at
        ),
        role:role_id (
          id,
          name
        ),
        branch:branch_id (
          id,
          name
        )
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update user info (name, etc)
export const updateUser = async (id, userData) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update(userData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete user role
//
// Goes through the delete-user edge function (service role) rather than a direct
// table delete. A client-side delete runs under RLS and silently removes zero
// rows when the caller lacks the users.update permission. The edge function also
// fully removes the account (login + profile) once it's the user's last branch,
// keeping the profile only when other records still reference it.
export const deleteUserRole = async id => {
  try {
    const {
      data: { session }
    } = await supabase.auth.getSession();

    if (!session) {
      return handleSupabaseError({ message: 'Not authenticated' });
    }

    const { data, error } = await supabase.functions.invoke('delete-user', {
      body: { userRoleId: id }
    });

    if (error) return handleSupabaseError(error);
    if (data?.error) return handleSupabaseError({ message: data.error });

    return { data: data?.data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              DASHBOARD                                     */
/* -------------------------------------------------------------------------- */

// Get donors count with optional filters
export const getDonorsCount = async (organisationId, branchId, dateFilter) => {
  try {
    let query = supabase
      .from('donars')
      .select('*', { count: 'exact', head: true })
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (dateFilter?.startDate && dateFilter?.endDate) {
      query = query
        .gte('created_at', dateFilter.startDate)
        .lte('created_at', dateFilter.endDate);
    }

    const { count, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data: count, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donation amount sum with optional filters
export const getDonationAmount = async (
  organisationId,
  branchId,
  paymentStatus = 'paid'
) => {
  try {
    let query = supabase
      .from('orders')
      .select('donation_amount')
      .eq('organisation_id', organisationId)
      .eq('payment_status', paymentStatus);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Calculate sum manually
    const sum =
      data?.reduce((acc, order) => acc + (order.donation_amount || 0), 0) || 0;

    return { data: sum, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get recent orders with all details
export const getRecentOrders = async (organisationId, branchId, limit = 10) => {
  try {
    let query = supabase
      .from('orders')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          email,
          primary_email
        ),
        payment_method:payment_method_id (
          payment_name
        ),
        order_details (
          billing_condition,
          donation_type:donation_type_id (
            donation_type_name
          ),
          package:package_id (
            package_title
          )
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('payment_status', 'paid')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get all orders with optional filters
export const getOrders = async (organisationId, branchId, filters = {}) => {
  try {
    let query = supabase
      .from('orders')
      .select(
        `
        *,
        is_recurring,
        stripe_subscription_id,
        donar:donar_id (
          id,
          first_name,
          last_name,
          email,
          primary_email
        ),
        payment_method:payment_method_id (
          payment_name
        ),
        order_details (
          billing_condition,
          amount,
          donation_type_id,
          package_id,
          donation_type:donation_type_id (
            donation_type_name
          ),
          package:package_id (
            package_title
          )
        ),
        refunds (
          reason,
          notes
        )
      `
      )
      .eq('organisation_id', organisationId)
      .in('payment_status', ['paid', 'refunded'])
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    // Apply filters if provided
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }
    if (filters.type) {
      query = query.eq('is_recurring', filters.type === 'monthly');
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get all recurring donations
export const getRecurringDonations = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    let query = supabase
      .from('recurring_donations')
      .select(
        `
        *,
        donor:donor_id(id, first_name, last_name, primary_email),
        branch:branch_id(name)
      `
      )
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query.order('created_at', {
      ascending: false
    });

    if (error) return handleSupabaseError(error);

    // Each subscription's monthly payments are orders rows keyed only by
    // stripe_subscription_id (no FK), so PostgREST can't embed them: fetch the
    // latest order per subscription separately for the details deep-link.
    const subIds = [
      ...new Set(data.map(rd => rd.stripe_subscription_id).filter(Boolean))
    ];
    const latestOrderBySub = {};
    if (subIds.length) {
      const { data: orders } = await supabase
        .from('orders')
        .select('id, stripe_subscription_id, created_at')
        .in('stripe_subscription_id', subIds)
        .order('created_at', { ascending: false });
      (orders || []).forEach(o => {
        if (!latestOrderBySub[o.stripe_subscription_id]) {
          latestOrderBySub[o.stripe_subscription_id] = o.id;
        }
      });
    }

    // cancelled_by references auth.users (staff OR donor), so no embed either.
    // A match in public.users means a staff member cancelled it; a non-null
    // id with no match is the donor's own account.
    const cancelledByIds = [
      ...new Set(data.map(rd => rd.cancelled_by).filter(Boolean))
    ];
    const staffNameById = {};
    if (cancelledByIds.length) {
      const { data: staff } = await supabase
        .from('users')
        .select('id, first_name, last_name')
        .in('id', cancelledByIds);
      (staff || []).forEach(u => {
        staffNameById[u.id] = [u.first_name, u.last_name]
          .filter(Boolean)
          .join(' ');
      });
    }

    const withNames = await resolveSnapshotItemNames(data);
    return {
      data: withNames.map(rd => ({
        ...rd,
        latest_order_id: latestOrderBySub[rd.stripe_subscription_id] || null,
        cancelled_by_staff_name: rd.cancelled_by
          ? staffNameById[rd.cancelled_by] || null
          : null
      })),
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get recurring donations for a specific donor
// order_details_snapshot only stores package_id / donation_type_id, so the
// item names are resolved here (the Giving tab shows names; this tab should
// match).
const resolveSnapshotItemNames = async subscriptions => {
  const packageIds = new Set();
  const typeIds = new Set();
  subscriptions.forEach(sub => {
    (Array.isArray(sub.order_details_snapshot)
      ? sub.order_details_snapshot
      : []
    ).forEach(item => {
      if (item.package_id) packageIds.add(item.package_id);
      if (item.donation_type_id) typeIds.add(item.donation_type_id);
    });
  });

  const [{ data: packages }, { data: types }] = await Promise.all([
    packageIds.size
      ? supabase
          .from('packages')
          .select('id, package_title')
          .in('id', [...packageIds])
      : { data: [] },
    typeIds.size
      ? supabase
          .from('donation_type')
          .select('id, donation_type_name')
          .in('id', [...typeIds])
      : { data: [] }
  ]);

  const packageNames = new Map(
    (packages || []).map(p => [p.id, p.package_title])
  );
  const typeNames = new Map(
    (types || []).map(t => [t.id, t.donation_type_name])
  );

  return subscriptions.map(sub => ({
    ...sub,
    order_details_snapshot: Array.isArray(sub.order_details_snapshot)
      ? sub.order_details_snapshot.map(item => ({
          ...item,
          package_title:
            item.package_title || packageNames.get(item.package_id) || null,
          donation_type_name:
            item.donation_type_name ||
            typeNames.get(item.donation_type_id) ||
            null
        }))
      : sub.order_details_snapshot
  }));
};

export const getDonorSubscriptions = async donorId => {
  try {
    const { data, error } = await supabase
      .from('recurring_donations')
      .select('*')
      .eq('donor_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data: await resolveSnapshotItemNames(data || []), error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single order with details
export const getOrderDetails = async orderId => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(
        `
        *,
        payment_method:payment_method_id (
          payment_name
        ),
        order_details (
          *,
          package:package_id (
            package_title,
            simple_id,
            location
          ),
          donation_type:donation_type_id (
            donation_type_name
          ),
          sponsorships:sponsorship!order_detail_id (
            id,
            status,
            coverage_percentage,
            start_date,
            orphan:orphan_id (
              id,
              simple_id,
              first_name,
              middle_name,
              last_name,
              country,
              image
            ),
            beneficiary_sponsorship_type:beneficiary_sponsorship_type_id (
              sponsorship_type:sponsorship_type_id (
                type
              )
            )
          )
        ),
        refunds (
          id,
          stripe_refund_id,
          amount,
          reason,
          notes,
          created_at,
          refunded_by_user:refunded_by (
            first_name,
            last_name
          )
        )
      `
      )
      .eq('id', orderId)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donations for a specific donor (for sponsorship form)
export const getDonorDonations = async (donorId, organisationId) => {
  try {
    if (!donorId) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('orders')
      .select(
        `
        id,
        donation_amount,
        payment_status,
        payment_method:payment_method_id (
          payment_name
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donation items (order details) for a specific donation/order
export const getDonationItems = async orderId => {
  try {
    if (!orderId) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('order_details')
      .select(
        `
        id,
        billing_condition,
        amount,
        donation_type:donation_type_id (
          donation_type_name
        )
      `
      )
      .eq('order_id', orderId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donor communications
export const getDonorCommunications = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donars_communication')
      .select('*')
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor communication
export const createDonorCommunication = async communicationData => {
  try {
    const { data, error } = await supabase
      .from('donars_communication')
      .insert([communicationData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         DONATION SETTINGS                                  */
/* -------------------------------------------------------------------------- */

// Get donation types
export const getDonationTypes = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('donation_type')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('donation_type_name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get packages with filters
export const getPackages = async (organisationId, branchId, filters = {}) => {
  try {
    let query = supabase
      .from('packages')
      .select('*')
      .eq('organisation_id', organisationId)
      .eq('is_hidden', false);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query
      .order('display_order')
      .order('package_title');

    if (error) return handleSupabaseError(error);

    // Apply JSONB filters in JavaScript since Supabase JSONB queries are complex
    let filteredData = data;

    if (filters.paymentOption && data) {
      filteredData = filteredData.filter(
        pkg =>
          pkg.payment_options &&
          pkg.payment_options.includes(filters.paymentOption)
      );
    }

    if (filters.donationTypeId && filteredData) {
      filteredData = filteredData.filter(
        pkg =>
          pkg.donation_type &&
          pkg.donation_type.includes(filters.donationTypeId)
      );
    }

    if (
      filters.locationIdList &&
      filters.locationIdList.length > 0 &&
      filteredData
    ) {
      filteredData = filteredData.filter(pkg => {
        if (!pkg.location) return false;
        return filters.locationIdList.some(locId =>
          pkg.location.includes(locId)
        );
      });
    }

    return { data: filteredData, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Bulk update package display order
export const updatePackageDisplayOrder = async updates => {
  try {
    for (const { id, display_order } of updates) {
      const { error } = await supabase
        .from('packages')
        .update({ display_order })
        .eq('id', id);
      if (error) return handleSupabaseError(error);
    }
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get transaction fees
export const getTransactionFees = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('transaction_fee')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get package categories
export const getPackageCategories = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('package_category')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('category_name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get campaigns
export const getCampaigns = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('campaign')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donation amounts
export const getDonationAmounts = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('donation_amount')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('amount');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donation currencies for an organization
export const getDonationCurrencies = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('donation_currency')
      .select(
        `
        *,
        donation_currency_organisation:organisation_id (
          currency:currency_id (
            id,
            abbreviation,
            currency_name,
            symbol
          )
        )
      `
      )
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('created_at', {
      ascending: false
    });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get website settings for an organization/branch
export const getWebsiteSettings = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('website_settings')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    } else {
      query = query.is('branch_id', null);
    }

    const { data, error } = await query.maybeSingle();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update website settings (upsert)
export const upsertWebsiteSettings = async settingsData => {
  try {
    const { data, error } = await supabase
      .from('website_settings')
      .upsert(settingsData, {
        onConflict: 'organisation_id,branch_id'
      })
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Custom donation links: short public-site URLs that preselect a campaign
// (package) and donation type. See migration 20261011120000_donation_links.
export const getDonationLinks = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('donation_links')
      .select(
        `
        id, slug, is_active, created_at, branch_id,
        package:package_id (id, package_title),
        donation_type:donation_type_id (id, donation_type_name)
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data: data || [], error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const createDonationLink = async linkData => {
  try {
    const { data, error } = await supabase
      .from('donation_links')
      .insert([linkData])
      .select()
      .single();

    if (error) {
      // Unique (organisation_id, slug)
      if (error.code === '23505')
        return { data: null, error: 'That link name is already in use' };
      return handleSupabaseError(error);
    }
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteDonationLink = async id => {
  try {
    const { error } = await supabase
      .from('donation_links')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update organization
export const updateOrganization = async (organizationId, data) => {
  try {
    const { data: result, error } = await supabase
      .from('organisations')
      .update(data)
      .eq('id', organizationId)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           BENEFICIARIES/ORPHANS                            */
/* -------------------------------------------------------------------------- */

// Get all orphans/beneficiaries
export const getBeneficiaries = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    let query = supabase
      .from('orphans')
      .select(
        `
        *,
        branch:branch_id (
          name
        ),
        sponsorship_condition:sponsorship_condition_id (
          condition
        ),
        sponsorship_priority:sponsorship_priority_id (
          priority_type
        ),
        sponsorship_type:sponsorship_type_id (
          type
        ),
        sponsorship_status:sponsorship_status_id (
          id,
          status,
          color,
          is_hidden
        ),
        sponsorships:sponsorship!orphan_id (
          id,
          start_date,
          duration,
          status
        ),
        beneficiary_sponsorship_types (
          id,
          sponsorship_type:sponsorship_type_id (
            type
          )
        ),
        school_students ( end_date, school:school_id ( school_name ) ),
        mosque_orphan ( end_date, mosque:mosque_id ( name ) ),
        hospital_orphan ( end_date, hospital:hospital_id ( name ) ),
        orphan_orphanage ( end_date, orphanage:orphanage_id ( name ) )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('is_hidden', false)
      .order('created_at', { ascending: true });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single beneficiary/orphan
export const getBeneficiary = async id => {
  try {
    const { data, error } = await supabase
      .from('orphans')
      .select(
        `
        *,
        guardian:guardian_id (
          id,
          first_name,
          middle_name,
          last_name,
          email,
          phone,
          address,
          father_status,
          mother_status,
          relationship,
          occupation,
          monthly_income_range,
          notes
        ),
        sponsorship_condition:sponsorship_condition_id (
          id,
          condition
        ),
        sponsorship_status:sponsorship_status_id (
          id,
          status
        ),
        sponsorship_priority:sponsorship_priority_id (
          id,
          priority_type
        ),
        beneficiary_sponsorship_types (
          id,
          cost,
          sponsorship_type:sponsorship_type_id (
            id,
            type
          )
        )
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);

    // Fetch lookup values for guardian status fields
    const guardianWithLookups = data?.guardian;
    if (guardianWithLookups) {
      // Fetch father status name
      if (guardianWithLookups.father_status) {
        const { data: fatherStatus } = await supabase
          .from('guardian_father_status')
          .select('id, name')
          .eq('id', guardianWithLookups.father_status)
          .single();
        guardianWithLookups.guardian_father_status = fatherStatus;
      }
      // Fetch mother status name
      if (guardianWithLookups.mother_status) {
        const { data: motherStatus } = await supabase
          .from('guardian_mother_status')
          .select('id, name')
          .eq('id', guardianWithLookups.mother_status)
          .single();
        guardianWithLookups.guardian_mother_status = motherStatus;
      }
      // Fetch relationship name
      if (guardianWithLookups.relationship) {
        const { data: relationshipData } = await supabase
          .from('guardian_relationship')
          .select('id, name')
          .eq('id', guardianWithLookups.relationship)
          .single();
        guardianWithLookups.guardian_relationShip = relationshipData;
      }
    }

    // Fetch sponsorship types for this beneficiary
    const { data: sponsorshipTypes } = await supabase
      .from('beneficiary_sponsorship_types')
      .select(
        `
        id,
        sponsorship_type_id,
        cost,
        duration,
        sponsorship_type:sponsorship_type_id (
          id,
          type
        )
      `
      )
      .eq('orphan_id', id);

    // Transform to match form structure
    // Include both IDs:
    // - typeId: sponsorship_type_id for form checkbox matching
    // - junctionId: beneficiary_sponsorship_types.id for sponsorship table FK
    const formattedSponsorshipTypes =
      sponsorshipTypes?.map(st => ({
        typeId: st.sponsorship_type_id, // For form checkbox matching
        junctionId: st.id, // Junction table PK for sponsorship FK
        typeName: st.sponsorship_type?.type || '',
        cost: st.cost?.toString() || ''
      })) || [];

    // Fetch parents (father/mother)
    const { data: parents } = await supabase
      .from('orphan_parents')
      .select('*')
      .eq('orphan_id', id);

    // Fetch next-of-kin (single)
    const { data: nokRow } = await supabase
      .from('orphan_next_of_kin')
      .select('*')
      .eq('orphan_id', id)
      .maybeSingle();

    return {
      data: {
        ...data,
        guardian: guardianWithLookups,
        sponsorship_types: formattedSponsorshipTypes,
        parents: parents || [],
        next_of_kin: nokRow || null
      },
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Helper: replace all parent rows for an orphan with the provided list (0–2 entries).
const persistOrphanParents = async (
  orphanId,
  parents,
  branchId,
  organisationId
) => {
  if (!Array.isArray(parents)) return;
  await supabase.from('orphan_parents').delete().eq('orphan_id', orphanId);
  const rows = parents
    .filter(
      p =>
        p &&
        p.parent_type &&
        (p.first_name || p.last_name || p.phone || p.address || p.status)
    )
    .map(p => ({
      orphan_id: orphanId,
      parent_type: p.parent_type,
      first_name: p.first_name || null,
      middle_name: p.middle_name || null,
      last_name: p.last_name || null,
      status: p.status || null,
      relationship: p.relationship || null,
      phone: p.phone || null,
      email: p.email || null,
      address: p.address || null,
      occupation: p.occupation || null,
      monthly_income_range: p.monthly_income_range || null,
      notes: p.notes || null,
      branch_id: branchId || null,
      organisation_id: organisationId || null
    }));
  if (rows.length === 0) return;
  const { error } = await supabase.from('orphan_parents').insert(rows);
  if (error) console.error('Error persisting parents:', error);
};

// Helper: upsert the single next-of-kin row for an orphan (or delete if empty).
const persistOrphanNextOfKin = async (
  orphanId,
  nok,
  branchId,
  organisationId
) => {
  if (nok === undefined) return;
  const hasContent =
    nok &&
    (nok.first_name || nok.last_name || nok.phone || nok.email || nok.address);
  if (!hasContent) {
    await supabase
      .from('orphan_next_of_kin')
      .delete()
      .eq('orphan_id', orphanId);
    return;
  }
  const row = {
    orphan_id: orphanId,
    first_name: nok.first_name || null,
    middle_name: nok.middle_name || null,
    last_name: nok.last_name || null,
    relationship: nok.relationship || null,
    phone: nok.phone || null,
    email: nok.email || null,
    address: nok.address || null,
    notes: nok.notes || null,
    branch_id: branchId || null,
    organisation_id: organisationId || null
  };
  const { error } = await supabase
    .from('orphan_next_of_kin')
    .upsert(row, { onConflict: 'orphan_id' });
  if (error) console.error('Error persisting next-of-kin:', error);
};

// Create beneficiary/orphan

// Names arrive with stray spaces (imports, copy-paste); a trailing space on
// first_name made full-name search miss. Normalise on every write.
const cleanName = v =>
  typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() || null : v;
const normaliseOrphanNames = orphanData => {
  ['first_name', 'middle_name', 'last_name'].forEach(k => {
    if (k in orphanData) orphanData[k] = cleanName(orphanData[k]);
  });
  return orphanData;
};

export const createBeneficiary = async beneficiaryData => {
  try {
    // Extract sponsorship_types, guardian, parents, next_of_kin from beneficiaryData
    const { sponsorship_types, guardian, parents, next_of_kin, ...orphanData } =
      beneficiaryData;
    normaliseOrphanNames(orphanData);

    // Create guardian first if guardian data is provided
    let guardianId = null;
    if (
      guardian &&
      (guardian.first_name || guardian.last_name || guardian.phone)
    ) {
      const { data: guardianData, error: guardianError } = await supabase
        .from('guardian')
        .insert([guardian])
        .select()
        .single();

      if (guardianError) {
        console.error('Error creating guardian:', guardianError);
        return handleSupabaseError(guardianError);
      }
      guardianId = guardianData?.id;
    }

    // Insert the orphan record with guardian_id
    const { data, error } = await supabase
      .from('orphans')
      .insert([{ ...orphanData, guardian_id: guardianId }])
      .select()
      .single();

    if (error) return handleSupabaseError(error);

    // Persist parents and next-of-kin (optional)
    if (data?.id) {
      await persistOrphanParents(
        data.id,
        parents,
        orphanData.branch_id,
        orphanData.organisation_id
      );
      await persistOrphanNextOfKin(
        data.id,
        next_of_kin,
        orphanData.branch_id,
        orphanData.organisation_id
      );
    }

    // If sponsorship_types are provided, insert them into the junction table
    if (sponsorship_types && sponsorship_types.length > 0 && data?.id) {
      // Look up costs from sponsorship_cost settings table
      const typeIds = sponsorship_types.map(st => st.typeId).filter(Boolean);
      const { data: costSettings } = await supabase
        .from('sponsorship_cost')
        .select('sponsorship_type_id, amount')
        .eq('organisation_id', orphanData.organisation_id)
        .in('sponsorship_type_id', typeIds);

      // Create a map of typeId -> cost from settings
      const costMap = {};
      costSettings?.forEach(cs => {
        costMap[cs.sponsorship_type_id] = cs.amount;
      });

      const sponsorshipTypesData = sponsorship_types.map(st => ({
        orphan_id: data.id,
        sponsorship_type_id: st.typeId,
        cost: costMap[st.typeId] || 0,
        duration: 'monthly'
      }));

      const { error: stError } = await supabase
        .from('beneficiary_sponsorship_types')
        .insert(sponsorshipTypesData);

      if (stError) {
        console.error('Error inserting sponsorship types:', stError);
        // Don't fail the whole operation, just log the error
      }
    }

    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update beneficiary/orphan
export const updateBeneficiary = async (
  id,
  beneficiaryData,
  userInfo = null
) => {
  try {
    // Extract sponsorship_types, guardian, parents, next_of_kin from beneficiaryData
    const { sponsorship_types, guardian, parents, next_of_kin, ...orphanData } =
      beneficiaryData;
    normaliseOrphanNames(orphanData);

    // First, get the current orphan record to find existing guardian_id and status values
    const { data: currentOrphan } = await supabase
      .from('orphans')
      .select(
        `
        guardian_id,
        branch_id,
        organisation_id,
        sponsorship_status_id,
        sponsorship_priority_id,
        sponsorship_status:sponsorship_status_id(id, status, color, is_hidden),
        sponsorship_priority:sponsorship_priority_id(id, priority_type)
      `
      )
      .eq('id', id)
      .single();

    // Handle guardian update/create
    let guardianId = currentOrphan?.guardian_id;
    if (
      guardian &&
      (guardian.first_name || guardian.last_name || guardian.phone)
    ) {
      if (guardianId) {
        // Update existing guardian
        const { error: guardianError } = await supabase
          .from('guardian')
          .update(guardian)
          .eq('id', guardianId);

        if (guardianError) {
          console.error('Error updating guardian:', guardianError);
        }
      } else {
        // Create new guardian
        const { data: guardianData, error: guardianError } = await supabase
          .from('guardian')
          .insert([guardian])
          .select()
          .single();

        if (guardianError) {
          console.error('Error creating guardian:', guardianError);
        } else {
          guardianId = guardianData?.id;
          orphanData.guardian_id = guardianId;
        }
      }
    }

    // Update the orphan record
    const { data, error } = await supabase
      .from('orphans')
      .update(orphanData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);

    // Persist parents and next-of-kin (optional)
    const branchForChildren = orphanData.branch_id || currentOrphan?.branch_id;
    const orgForChildren =
      orphanData.organisation_id || currentOrphan?.organisation_id;
    if (parents !== undefined) {
      await persistOrphanParents(
        id,
        parents,
        branchForChildren,
        orgForChildren
      );
    }
    if (next_of_kin !== undefined) {
      await persistOrphanNextOfKin(
        id,
        next_of_kin,
        branchForChildren,
        orgForChildren
      );
    }

    // If sponsorship_types are provided, update the junction table.
    // Diff against the existing rows instead of delete-all + reinsert:
    // sponsorship.beneficiary_sponsorship_type_id points at these rows (FK ON
    // DELETE SET NULL), so wiping them severed every existing sponsorship from
    // its type and made sponsors vanish from the beneficiary's Donors tab.
    if (sponsorship_types !== undefined) {
      const selectedTypeIds = (sponsorship_types || [])
        .map(st => st.typeId)
        .filter(Boolean);

      const { data: existingTypes } = await supabase
        .from('beneficiary_sponsorship_types')
        .select('id, sponsorship_type_id')
        .eq('orphan_id', id);

      const existingByTypeId = new Map(
        (existingTypes || []).map(row => [row.sponsorship_type_id, row])
      );

      // Remove only the types that were deselected
      const removedIds = (existingTypes || [])
        .filter(row => !selectedTypeIds.includes(row.sponsorship_type_id))
        .map(row => row.id);
      if (removedIds.length > 0) {
        const { error: delError } = await supabase
          .from('beneficiary_sponsorship_types')
          .delete()
          .in('id', removedIds);
        if (delError) {
          console.error('Error removing sponsorship types:', delError);
        }
      }

      // Insert only the newly added types
      const addedTypeIds = selectedTypeIds.filter(
        typeId => !existingByTypeId.has(typeId)
      );
      if (addedTypeIds.length > 0) {
        // Look up costs from sponsorship_cost settings table
        const { data: costSettings } = await supabase
          .from('sponsorship_cost')
          .select('sponsorship_type_id, amount')
          .eq(
            'organisation_id',
            orphanData.organisation_id || currentOrphan?.organisation_id
          )
          .in('sponsorship_type_id', addedTypeIds);

        const costMap = {};
        costSettings?.forEach(cs => {
          costMap[cs.sponsorship_type_id] = cs.amount;
        });

        const sponsorshipTypesData = addedTypeIds.map(typeId => ({
          orphan_id: id,
          sponsorship_type_id: typeId,
          cost: costMap[typeId] || 0,
          duration: 'monthly'
        }));

        const { error: stError } = await supabase
          .from('beneficiary_sponsorship_types')
          .insert(sponsorshipTypesData);

        if (stError) {
          console.error('Error updating sponsorship types:', stError);
        }
      }
    }

    // Log status changes if userInfo is provided
    if (userInfo && currentOrphan) {
      const branchId = currentOrphan.branch_id || orphanData.branch_id;

      // Check if sponsorship_status_id changed
      if (
        orphanData.sponsorship_status_id !== undefined &&
        orphanData.sponsorship_status_id !== currentOrphan.sponsorship_status_id
      ) {
        // Get the new status name
        let newStatusName = null;
        if (orphanData.sponsorship_status_id) {
          const { data: newStatus } = await supabase
            .from('sponsorship_status')
            .select('status')
            .eq('id', orphanData.sponsorship_status_id)
            .single();
          newStatusName = newStatus?.status;
        }

        await insertBeneficiaryStatusLog({
          beneficiary_id: id,
          field_name: 'sponsorship_status',
          old_value: currentOrphan.sponsorship_status?.status || null,
          new_value: newStatusName,
          old_value_id: currentOrphan.sponsorship_status_id,
          new_value_id: orphanData.sponsorship_status_id,
          changed_by_user_id: userInfo.id,
          changed_by_name:
            `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() ||
            'Unknown User',
          branch_id: branchId
        });
      }

      // Check if sponsorship_priority_id changed
      if (
        orphanData.sponsorship_priority_id !== undefined &&
        orphanData.sponsorship_priority_id !==
          currentOrphan.sponsorship_priority_id
      ) {
        // Get the new priority name
        let newPriorityName = null;
        if (orphanData.sponsorship_priority_id) {
          const { data: newPriority } = await supabase
            .from('sponsorship_priority')
            .select('priority_type')
            .eq('id', orphanData.sponsorship_priority_id)
            .single();
          newPriorityName = newPriority?.priority_type;
        }

        await insertBeneficiaryStatusLog({
          beneficiary_id: id,
          field_name: 'priority_status',
          old_value: currentOrphan.sponsorship_priority?.priority_type || null,
          new_value: newPriorityName,
          old_value_id: currentOrphan.sponsorship_priority_id,
          new_value_id: orphanData.sponsorship_priority_id,
          changed_by_user_id: userInfo.id,
          changed_by_name:
            `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() ||
            'Unknown User',
          branch_id: branchId
        });
      }
    }

    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete beneficiary/orphan (soft delete)
export const deleteBeneficiary = async id => {
  try {
    const { data, error } = await supabase
      .from('orphans')
      .update({ is_hidden: true })
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                       BENEFICIARY FEEDBACK / NOTES                         */
/* -------------------------------------------------------------------------- */

const BENEFICIARY_FEEDBACK_SELECT = `
  id,
  description,
  orphan_id,
  priority,
  note_type,
  is_pinned,
  created_at,
  user:user_id (
    first_name,
    last_name
  )
`;

export const getBeneficiaryFeedback = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('orphans_feedback')
      .select(BENEFICIARY_FEEDBACK_SELECT)
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const createBeneficiaryFeedback = async feedbackData => {
  try {
    const { data, error } = await supabase
      .from('orphans_feedback')
      .insert([feedbackData])
      .select(BENEFICIARY_FEEDBACK_SELECT)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateBeneficiaryFeedback = async (id, feedbackData) => {
  try {
    const { data, error } = await supabase
      .from('orphans_feedback')
      .update(feedbackData)
      .eq('id', id)
      .select(BENEFICIARY_FEEDBACK_SELECT)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteBeneficiaryFeedback = async id => {
  try {
    const { error } = await supabase
      .from('orphans_feedback')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    BENEFICIARY SUPPORTING DOCUMENTS                        */
/* -------------------------------------------------------------------------- */

const SUPPORTING_DOC_SELECT = `
  id,
  orphan_id,
  document_type,
  file_url,
  file_name,
  file_size,
  approved,
  approved_at,
  created_at,
  uploaded_by_user:uploaded_by (
    id,
    first_name,
    last_name
  ),
  approved_by_user:approved_by (
    id,
    first_name,
    last_name
  )
`;

export const getBeneficiarySupportingDocs = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('orphans_supporting_documents')
      .select(SUPPORTING_DOC_SELECT)
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const createBeneficiarySupportingDoc = async docData => {
  try {
    const { data, error } = await supabase
      .from('orphans_supporting_documents')
      .insert([docData])
      .select(SUPPORTING_DOC_SELECT)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const approveBeneficiarySupportingDoc = async (id, approvedByUserId) => {
  try {
    const { data, error } = await supabase
      .from('orphans_supporting_documents')
      .update({
        approved: true,
        approved_by: approvedByUserId,
        approved_at: new Date().toISOString()
      })
      .eq('id', id)
      .select(SUPPORTING_DOC_SELECT)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteBeneficiarySupportingDoc = async id => {
  try {
    const { error } = await supabase
      .from('orphans_supporting_documents')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           SPONSORSHIPS                                     */
/* -------------------------------------------------------------------------- */

// Get all sponsorships
export const getSponsorships = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          email,
          primary_email
        ),
        orphan:orphan_id!inner (
          id,
          first_name,
          last_name,
          middle_name,
          country,
          image,
          DOB,
          gender,
          branch_id
        )
      `
      )
      .order('created_at', { ascending: false });

    // Filter by branch through the orphan join (avoids a huge in() id list,
    // which fails past ~640 ids - see selectInChunks)
    if (branchId) {
      query = query.eq('orphan.branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single sponsorship
export const getSponsorship = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .select(
        `
        *,
        donar:donar_id (*),
        orphan:orphan_id (*)
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create sponsorship
export const createSponsorship = async sponsorshipData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .insert([sponsorshipData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update sponsorship
export const updateSponsorship = async (id, sponsorshipData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .update(sponsorshipData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete sponsorship
export const deleteSponsorship = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP MANAGEMENT (Shared Sponsorships)            */
/* -------------------------------------------------------------------------- */

// Get sponsorship management stats for dashboard cards
export const getSponsorshipManagementStats = async (
  organisationId,
  branchId
) => {
  try {
    let query = supabase
      .from('sponsorship')
      .select(
        `
        id,
        status,
        orphan_id,
        orphans!inner (
          organisation_id,
          branch_id
        )
      `
      )
      .eq('orphans.organisation_id', organisationId);

    if (branchId) {
      query = query.eq('orphans.branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Calculate stats
    const stats = {
      total: data?.length || 0,
      active: data?.filter(s => s.status === 'active').length || 0,
      pending: data?.filter(s => s.status === 'pending').length || 0,
      ended: data?.filter(s => s.status === 'ended').length || 0,
      uniqueBeneficiaries: new Set(data?.map(s => s.orphan_id)).size || 0
    };

    return { data: stats, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get all sponsorships with full details for management page
export const getSponsorshipsForManagement = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    // First get all beneficiaries with their sponsorship types
    let beneficiaryQuery = supabase
      .from('orphans')
      .select(
        `
        id,
        simple_id,
        first_name,
        middle_name,
        last_name,
        country,
        branch_id,
        branch:branch_id (
          name
        ),
        sponsorship_status:sponsorship_status_id (
          status,
          color,
          is_hidden
        ),
        beneficiary_sponsorship_types (
          id,
          cost,
          duration,
          sponsorship_type:sponsorship_type_id (
            id,
            type
          )
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('is_hidden', false);

    if (branchId) {
      beneficiaryQuery = beneficiaryQuery.eq('branch_id', branchId);
    }

    const { data: beneficiaries, error: beneficiaryError } =
      await beneficiaryQuery;
    if (beneficiaryError) return handleSupabaseError(beneficiaryError);

    // Get all sponsorships with donor details (batched - see selectInChunks)
    const { data: sponsorships, error: sponsorshipError } =
      await selectInChunks(
        () =>
          supabase.from('sponsorship').select(
            `
        id,
        simple_id,
        orphan_id,
        donar_id,
        beneficiary_sponsorship_type_id,
        start_date,
        duration,
        allocated_amount,
        coverage_percentage,
        status,
        donor:donar_id (
          id,
          simple_id,
          first_name,
          last_name,
          primary_email
        ),
        order_detail:order_detail_id (
          billing_condition,
          order:order_id ( stripe_subscription_id )
        )
      `
          ),
        'orphan_id',
        beneficiaries.map(b => b.id)
      );

    if (sponsorshipError) return handleSupabaseError(sponsorshipError);

    // Live end dates (#8/#22): a monthly subscription keeps a sponsorship going
    // month by month, so its real end is the latest PAID payment + 1 month (or
    // the cancellation date), not start + duration which never moves.
    const subIds = [
      ...new Set(
        (sponsorships || [])
          .map(s => s.order_detail?.order?.stripe_subscription_id)
          .filter(Boolean)
      )
    ];
    const liveEndBySub = {};
    const paidToBySub = {};
    const cancelledSubs = new Set();
    if (subIds.length) {
      const [{ data: subs }, { data: paidOrders }] = await Promise.all([
        selectInChunks(
          () =>
            supabase
              .from('recurring_donations')
              .select('stripe_subscription_id, status, cancelled_at'),
          'stripe_subscription_id',
          subIds
        ),
        selectInChunks(
          () =>
            supabase
              .from('orders')
              .select('stripe_subscription_id, created_at')
              .eq('payment_status', 'paid'),
          'stripe_subscription_id',
          subIds
        )
      ]);
      const latestPaid = {};
      (paidOrders || []).forEach(o => {
        const t = new Date(o.created_at).getTime();
        if (
          !latestPaid[o.stripe_subscription_id] ||
          t > latestPaid[o.stripe_subscription_id]
        )
          latestPaid[o.stripe_subscription_id] = t;
      });
      (subs || []).forEach(sub => {
        if (latestPaid[sub.stripe_subscription_id]) {
          paidToBySub[sub.stripe_subscription_id] = new Date(
            latestPaid[sub.stripe_subscription_id]
          );
        }
        if (sub.status === 'cancelled') {
          cancelledSubs.add(sub.stripe_subscription_id);
          if (sub.cancelled_at)
            liveEndBySub[sub.stripe_subscription_id] = new Date(
              sub.cancelled_at
            );
        }
      });
    }
    const subOf = s => s.order_detail?.order?.stripe_subscription_id;
    // An active monthly sponsorship has no end date - it is ongoing while the
    // donor keeps paying. Only fixed-term (one-off, duration-based) or
    // cancelled-subscription sponsorships get a real end date.
    const isOngoing = s =>
      s.status === 'active' &&
      (s.order_detail?.billing_condition === 'monthly' ||
        (subOf(s) && !cancelledSubs.has(subOf(s))));
    const liveEndFor = s => liveEndBySub[subOf(s)] || null;
    const paidToFor = s => paidToBySub[subOf(s)] || null;

    // Flatten: one row per beneficiary-sponsorship-type combination
    const result = [];

    beneficiaries.forEach(beneficiary => {
      const beneficiarySponsorships =
        sponsorships?.filter(s => s.orphan_id === beneficiary.id) || [];

      const beneficiaryName = `${beneficiary.first_name} ${
        beneficiary.middle_name || ''
      } ${beneficiary.last_name}`.trim();

      // Create a row for each sponsorship type
      (beneficiary.beneficiary_sponsorship_types || []).forEach(bst => {
        // Get sponsorships for this specific type
        const typeActiveSponsorships = beneficiarySponsorships.filter(
          s =>
            s.beneficiary_sponsorship_type_id === bst.id &&
            s.status === 'active'
        );

        // Calculate coverage for this type
        const typeCoverage = typeActiveSponsorships.reduce(
          (sum, s) => sum + (s.coverage_percentage || 0),
          0
        );

        // Get donors for this type
        const typeDonors = typeActiveSponsorships
          .map(s => s.donor)
          .filter(Boolean);

        const donorCount = new Set(typeActiveSponsorships.map(s => s.donar_id))
          .size;

        // Get dates for this type
        let startDate = null;
        let endDate = null;

        if (typeActiveSponsorships.length > 0) {
          const dates = typeActiveSponsorships.map(s => {
            const planned = new Date(
              new Date(s.start_date).setMonth(
                new Date(s.start_date).getMonth() + (s.duration || 0)
              )
            );
            const live = liveEndFor(s);
            return {
              start: new Date(s.start_date),
              end: live && live > planned ? live : planned
            };
          });
          startDate = new Date(Math.min(...dates.map(d => d.start)));
          endDate = new Date(Math.max(...dates.map(d => d.end)));
        }
        const ongoing = typeActiveSponsorships.some(isOngoing);
        const paidToDates = typeActiveSponsorships
          .map(paidToFor)
          .filter(Boolean);
        const paidTo = paidToDates.length
          ? new Date(Math.max(...paidToDates))
          : null;

        // Children who have left the programme (grey statuses) are not
        // candidates for sponsorship management
        if (beneficiary.sponsorship_status?.is_hidden) return;

        result.push({
          id: `${beneficiary.id}-${bst.id}`, // Unique ID for each row
          beneficiaryId: beneficiary.id,
          childStatus: beneficiary.sponsorship_status?.status || null,
          childStatusColor: beneficiary.sponsorship_status?.color || null,
          beneficiarySponsorshipTypeId: bst.id,
          simpleId: beneficiary.simple_id,
          name: beneficiaryName,
          country: beneficiary.country,
          branchName: beneficiary.branch?.name || null,
          sponsorshipType: bst.sponsorship_type?.type || 'Sponsorship',
          monthlyCost: bst.cost || 0,
          totalCoverage: Math.min(100, typeCoverage),
          donorCount,
          donors: typeDonors,
          sponsorships: typeActiveSponsorships,
          startDate,
          // null while ongoing (see isOngoing); the UI shows "Ongoing"
          endDate: ongoing ? null : endDate,
          ongoing,
          paidTo,
          status:
            typeCoverage >= 100
              ? 'active'
              : typeCoverage > 0
              ? 'partial'
              : 'unsponsored'
        });
      });
    });

    // Apply filters
    let filteredResult = result;
    if (filters.status) {
      filteredResult = filteredResult.filter(r => r.status === filters.status);
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredResult = filteredResult.filter(
        r =>
          r.name.toLowerCase().includes(searchLower) ||
          r.donors.some(d =>
            `${d?.first_name} ${d?.last_name}`
              .toLowerCase()
              .includes(searchLower)
          )
      );
    }

    return { data: filteredResult, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get detailed sponsorship info for a specific beneficiary (for modal)
export const getSponsorshipDetail = async beneficiaryId => {
  try {
    // Get beneficiary with sponsorship types
    const { data: beneficiary, error: beneficiaryError } = await supabase
      .from('orphans')
      .select(
        `
        id,
        simple_id,
        first_name,
        middle_name,
        last_name,
        DOB,
        country,
        gender,
        branch_id,
        beneficiary_sponsorship_types (
          id,
          cost,
          duration,
          sponsorship_type:sponsorship_type_id (
            id,
            type,
            color
          )
        )
      `
      )
      .eq('id', beneficiaryId)
      .single();

    if (beneficiaryError) return handleSupabaseError(beneficiaryError);

    // Get all sponsorships for this beneficiary with full donor details
    const { data: sponsorships, error: sponsorshipError } = await supabase
      .from('sponsorship')
      .select(
        `
        id,
        simple_id,
        beneficiary_sponsorship_type_id,
        start_date,
        duration,
        allocated_amount,
        coverage_percentage,
        status,
        created_at,
        order_detail_id,
        donor:donar_id (
          id,
          simple_id,
          first_name,
          last_name,
          primary_email,
          primary_phone
        ),
        order_detail:order_detail_id (
          id,
          amount,
          billing_condition
        )
      `
      )
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (sponsorshipError) return handleSupabaseError(sponsorshipError);

    // Group sponsorships by beneficiary_sponsorship_type
    const sponsorshipsByType = {};
    beneficiary.beneficiary_sponsorship_types?.forEach(bst => {
      const typeSponshorships =
        sponsorships?.filter(
          s => s.beneficiary_sponsorship_type_id === bst.id
        ) || [];

      const activeSponsorships = typeSponshorships.filter(
        s => s.status === 'active'
      );
      const totalCoverage = activeSponsorships.reduce(
        (sum, s) => sum + (s.coverage_percentage || 0),
        0
      );

      sponsorshipsByType[bst.id] = {
        sponsorshipType: bst,
        sponsorships: typeSponshorships,
        activeSponsorships,
        totalCoverage: Math.min(100, totalCoverage),
        remainingCoverage: Math.max(0, 100 - totalCoverage),
        monthlyAmountCovered: (bst.cost * Math.min(100, totalCoverage)) / 100,
        monthlyAmountNeeded: (bst.cost * Math.max(0, 100 - totalCoverage)) / 100
      };
    });

    return {
      data: {
        beneficiary,
        sponsorshipsByType,
        allSponsorships: sponsorships
      },
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create a shared sponsorship (add a donor to a beneficiary's sponsorship type)
export const createSharedSponsorship = async ({
  orphanId,
  donorId,
  beneficiarySponsorshipTypeId,
  orderDetailId,
  coveragePercentage,
  allocatedAmount,
  startDate,
  duration,
  branchId
}) => {
  try {
    // Create the sponsorship record
    const { data: sponsorship, error: sponsorshipError } = await supabase
      .from('sponsorship')
      .insert([
        {
          orphan_id: orphanId,
          donar_id: donorId,
          beneficiary_sponsorship_type_id: beneficiarySponsorshipTypeId,
          order_detail_id: orderDetailId,
          coverage_percentage: coveragePercentage,
          allocated_amount: allocatedAmount,
          start_date: startDate,
          duration: duration,
          status: 'active',
          branch_id: branchId
        }
      ])
      .select()
      .single();

    if (sponsorshipError) return handleSupabaseError(sponsorshipError);

    // Note: a donation's remaining/allocated balance is derived on read from
    // its active sponsorship rows (the single source of truth), so there is no
    // cached allocated_amount/remaining_amount on order_details to maintain here.

    return { data: sponsorship, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Validate donation balances before creating sponsorships
// Returns { valid: boolean, errors: [], validatedItems: [] }
export const validateSponsorshipItems = async items => {
  try {
    const errors = [];
    const validatedItems = [];

    // Group items by orderDetailId to calculate total allocation per donation
    const allocationsByDonation = items.reduce((acc, item) => {
      if (!acc[item.orderDetailId]) {
        acc[item.orderDetailId] = {
          totalAllocation: 0,
          items: [],
          donorName: item.donorName
        };
      }
      acc[item.orderDetailId].totalAllocation += item.allocatedAmount;
      acc[item.orderDetailId].items.push(item);
      return acc;
    }, {});

    // Fetch current remaining amounts for all donations
    const orderDetailIds = Object.keys(allocationsByDonation);
    const { data: currentBalances, error: fetchError } = await supabase
      .from('order_details')
      .select(
        `id, amount,
        sponsorships:sponsorship!order_detail_id (
          allocated_amount,
          status
        )`
      )
      .in('id', orderDetailIds);

    if (fetchError) {
      return {
        valid: false,
        errors: [
          { type: 'fetch', message: 'Failed to validate donation balances' }
        ],
        validatedItems: []
      };
    }

    // Check each donation has sufficient balance
    for (const orderDetailId of orderDetailIds) {
      const allocation = allocationsByDonation[orderDetailId];
      const currentBalance = currentBalances.find(b => b.id === orderDetailId);

      if (!currentBalance) {
        errors.push({
          type: 'not_found',
          orderDetailId,
          donorName: allocation.donorName,
          message: `Donation not found for ${allocation.donorName}`
        });
        continue;
      }

      // Derive the available balance from the live ACTIVE sponsorships
      // attached to this donation rather than the stored allocated_amount/
      // remaining_amount columns, which can be stale (legacy/import
      // sponsorships never updated them, and ending a sponsorship does not
      // restore them). This keeps the creation guard consistent with the
      // Select-Donation modal and prevents over-allocation.
      const activeAllocated = (currentBalance.sponsorships || [])
        .filter(sp => sp.status === 'active')
        .reduce((sum, sp) => sum + (Number(sp.allocated_amount) || 0), 0);
      const availableAmount =
        Math.round(
          ((Number(currentBalance.amount) || 0) - activeAllocated) * 100
        ) / 100;

      if (allocation.totalAllocation > availableAmount) {
        errors.push({
          type: 'insufficient',
          orderDetailId,
          donorName: allocation.donorName,
          required: allocation.totalAllocation,
          available: availableAmount,
          message: `Insufficient balance for ${
            allocation.donorName
          }: needs £${allocation.totalAllocation.toFixed(
            2
          )} but only £${availableAmount.toFixed(2)} available`
        });
      } else {
        // Valid - add items with current balance info
        allocation.items.forEach(item => {
          validatedItems.push({
            ...item,
            currentAvailableAmount: availableAmount
          });
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      validatedItems: errors.length === 0 ? validatedItems : []
    };
  } catch (error) {
    return {
      valid: false,
      errors: [{ type: 'exception', message: error.message }],
      validatedItems: []
    };
  }
};

// Create multiple sponsorships in batch (validates first, then creates all)
export const createSponsorshipsBatch = async (
  items,
  beneficiaryId,
  branchId
) => {
  try {
    console.log('createSponsorshipsBatch called with:', {
      items,
      beneficiaryId,
      branchId
    });

    // First validate all items
    const validation = await validateSponsorshipItems(items);
    console.log('Validation result:', validation);
    if (!validation.valid) {
      return { data: null, error: validation.errors, validationFailed: true };
    }

    // Create all sponsorships
    const createdSponsorships = [];

    for (const item of items) {
      console.log('Creating sponsorship for item:', item);
      // Create the sponsorship record
      const { data: sponsorship, error: sponsorshipError } = await supabase
        .from('sponsorship')
        .insert([
          {
            orphan_id: beneficiaryId,
            donar_id: item.donorId,
            beneficiary_sponsorship_type_id: item.beneficiarySponsorshipTypeId,
            order_detail_id: item.orderDetailId,
            coverage_percentage: item.coveragePercentage,
            allocated_amount: item.allocatedAmount,
            start_date: item.startDate,
            duration: item.duration,
            status: 'active',
            branch_id: branchId
          }
        ])
        .select()
        .single();

      console.log('Sponsorship insert result:', {
        sponsorship,
        sponsorshipError
      });

      if (sponsorshipError) {
        // If any creation fails, we should ideally rollback, but Supabase doesn't support transactions
        // For now, return error with partial success info
        console.error('Sponsorship creation failed:', sponsorshipError);
        return {
          data: createdSponsorships,
          error: `Failed to create sponsorship: ${sponsorshipError.message}`,
          partialSuccess: createdSponsorships.length > 0
        };
      }

      createdSponsorships.push(sponsorship);
    }

    // Note: donation balances (remaining/allocated) are derived on read from
    // the active sponsorship rows just created — the single source of truth —
    // so there are no cached order_details columns to update here.

    console.log(
      'Batch creation complete. Created:',
      createdSponsorships.length,
      'sponsorships'
    );

    // Update beneficiary's sponsorship status based on total coverage
    await updateBeneficiarySponsorshipStatus(beneficiaryId);

    return { data: createdSponsorships, error: null };
  } catch (error) {
    console.error('createSponsorshipsBatch error:', error);
    return handleSupabaseError(error);
  }
};

// Update beneficiary sponsorship status based on total coverage.
// Auto rules (agreed 2026-08-24):
//   Fully Sponsored        = coverage >= 100%
//   Partial Sponsor Needed = 1-99%
//   Urgent Sponsor Needed  = 0% but the child HAS been sponsored before
//                            (a sponsor lapsed -> replacement needed)
//   Ready For Sponsorship  = 0% and never sponsored
// Manual statuses (Over 18 Support, Under Review, On Hold and the grey
// "left the programme" ones) are set by staff and are never overridden here.
export const updateBeneficiarySponsorshipStatus = async beneficiaryId => {
  try {
    // All sponsorships, not just active: ended ones tell us the child was
    // sponsored before, which separates Urgent from Ready.
    const { data: sponsorships, error: sponsorshipsError } = await supabase
      .from('sponsorship')
      .select('coverage_percentage, status')
      .eq('orphan_id', beneficiaryId);

    if (sponsorshipsError) {
      console.error('Error fetching sponsorships:', sponsorshipsError);
      return;
    }

    const activeSponsorships = (sponsorships || []).filter(
      s => s.status === 'active'
    );
    const totalCoverage = activeSponsorships.reduce(
      (sum, s) => sum + (Number(s.coverage_percentage) || 0),
      0
    );
    const wasSponsoredBefore = (sponsorships || []).length > 0;

    // Get the beneficiary to find their organisation_id, branch_id, and current status
    const { data: beneficiary } = await supabase
      .from('orphans')
      .select(
        `
        organisation_id,
        branch_id,
        sponsorship_status_id,
        sponsorship_status:sponsorship_status_id(id, status, is_auto)
      `
      )
      .eq('id', beneficiaryId)
      .single();

    if (!beneficiary) return;

    // A manually set status sticks until staff change it
    if (
      beneficiary.sponsorship_status &&
      beneficiary.sponsorship_status.is_auto === false
    ) {
      return;
    }

    // Determine status name based on coverage
    let statusName;
    if (totalCoverage >= 100) {
      statusName = 'Fully Sponsored';
    } else if (totalCoverage > 0) {
      statusName = 'Partial Sponsor Needed';
    } else if (wasSponsoredBefore) {
      statusName = 'Urgent Sponsor Needed';
    } else {
      statusName = 'Ready For Sponsorship';
    }

    // Look up the status ID
    let statusQuery = supabase
      .from('sponsorship_status')
      .select('id, status')
      .eq('organisation_id', beneficiary.organisation_id)
      .ilike('status', statusName);

    if (beneficiary.branch_id) {
      statusQuery = statusQuery.or(
        `branch_id.eq.${beneficiary.branch_id},branch_id.is.null`
      );
    }

    const { data: statusData } = await statusQuery.limit(1).single();

    if (statusData?.id) {
      // Only update if status is actually changing
      if (statusData.id !== beneficiary.sponsorship_status_id) {
        // Update beneficiary's sponsorship_status_id
        const { error: updateError } = await supabase
          .from('orphans')
          .update({ sponsorship_status_id: statusData.id })
          .eq('id', beneficiaryId);

        if (updateError) {
          console.error('Error updating beneficiary status:', updateError);
        } else {
          console.log(`Beneficiary status updated to: ${statusName}`);

          // Log the automatic status change
          await insertBeneficiaryStatusLog({
            beneficiary_id: beneficiaryId,
            field_name: 'sponsorship_status',
            old_value: beneficiary.sponsorship_status?.status || null,
            new_value: statusData.status,
            old_value_id: beneficiary.sponsorship_status_id,
            new_value_id: statusData.id,
            changed_by_user_id: null,
            changed_by_name: 'System (Auto-update)',
            branch_id: beneficiary.branch_id
          });
        }
      }
    } else {
      console.warn(`Status "${statusName}" not found for organisation`);
    }
  } catch (error) {
    console.error('Error updating beneficiary sponsorship status:', error);
  }
};

// End a sponsorship
export const endSponsorship = async sponsorshipId => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .update({ status: 'ended' })
      .eq('id', sponsorshipId)
      .select()
      .single();

    if (error) return handleSupabaseError(error);

    // Update beneficiary's sponsorship status after ending
    if (data?.orphan_id) {
      await updateBeneficiarySponsorshipStatus(data.orphan_id);
    }

    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Change which donation funds a sponsorship, keeping the sponsorship row (and
// its id/start date/duration history) intact. This is the alternative to the
// old destructive path of endSponsorship + create-new, which lost the record.
export const changeSponsorshipPayment = async ({
  sponsorshipId,
  newOrderDetailId,
  actor = {}
}) => {
  try {
    const { data: sponsorship, error: sponsorshipError } = await supabase
      .from('sponsorship')
      .select(
        `
        id,
        orphan_id,
        donar_id,
        allocated_amount,
        branch_id,
        status,
        order_detail_id,
        donor:donar_id (first_name, last_name, simple_id),
        order_detail:order_detail_id (id, amount)
      `
      )
      .eq('id', sponsorshipId)
      .single();

    if (sponsorshipError) return handleSupabaseError(sponsorshipError);

    if (sponsorship.status !== 'active') {
      return {
        data: null,
        error: 'Only active sponsorships can have their payment changed'
      };
    }
    if (sponsorship.order_detail_id === newOrderDetailId) {
      return {
        data: null,
        error: 'This sponsorship is already funded by that donation'
      };
    }

    // Same capacity rule as when creating a sponsorship: derive the new
    // donation's remaining balance from its live ACTIVE sponsorships rather
    // than the stored allocated/remaining columns, which can be stale (see
    // getDonorsWithRemainingDonations). Exclude this sponsorship itself so a
    // partial move within the same donation line is not double-counted.
    const { data: newDetail, error: detailError } = await supabase
      .from('order_details')
      .select(
        `
        id,
        amount,
        order:order_id (
          payment_status,
          donor:donar_id (id, first_name, last_name, simple_id)
        ),
        sponsorships:sponsorship!order_detail_id (id, allocated_amount, status)
      `
      )
      .eq('id', newOrderDetailId)
      .single();

    if (detailError) return handleSupabaseError(detailError);

    if (newDetail.order?.payment_status !== 'paid') {
      return { data: null, error: 'That donation has not been paid' };
    }

    const activeAllocated = (newDetail.sponsorships || [])
      .filter(sp => sp.status === 'active' && sp.id !== sponsorshipId)
      .reduce((sum, sp) => sum + (Number(sp.allocated_amount) || 0), 0);
    const remaining =
      Math.round(((Number(newDetail.amount) || 0) - activeAllocated) * 100) /
      100;
    const needed = Number(sponsorship.allocated_amount) || 0;

    if (remaining < needed) {
      return {
        data: null,
        error: `That donation only has ${remaining.toFixed(
          2
        )} unallocated but this sponsorship needs ${needed.toFixed(2)}`
      };
    }

    const newDonor = newDetail.order?.donor;
    const { data: updated, error: updateError } = await supabase
      .from('sponsorship')
      .update({
        order_detail_id: newOrderDetailId,
        donar_id: newDonor?.id || null
      })
      .eq('id', sponsorshipId)
      .select()
      .single();

    if (updateError) return handleSupabaseError(updateError);

    // Audit trail on the beneficiary's status log (old/new order_detail ids in
    // the value-id columns), so the change shows up with who did it and when.
    const describeSource = (donor, amount) => {
      const name =
        `${donor?.first_name || ''} ${donor?.last_name || ''}`.trim() ||
        'Unknown donor';
      const ref = donor?.simple_id ? ` (D${donor.simple_id})` : '';
      const amt =
        amount !== null && amount !== undefined
          ? `, ${Number(amount).toFixed(2)}`
          : '';
      return `${name}${ref}${amt}`;
    };

    await insertBeneficiaryStatusLog({
      beneficiary_id: sponsorship.orphan_id,
      field_name: 'sponsorship_payment',
      old_value: describeSource(
        sponsorship.donor,
        sponsorship.order_detail?.amount
      ),
      new_value: describeSource(newDonor, newDetail.amount),
      old_value_id: sponsorship.order_detail_id,
      new_value_id: newOrderDetailId,
      changed_by_user_id: actor.userId || null,
      changed_by_name: actor.userName || null,
      branch_id: sponsorship.branch_id
    });

    return { data: updated, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donors with remaining donation amounts (for adding sponsors)
export const getDonorsWithRemainingDonations = async (
  organisationId,
  branchId,
  { includeAll = false } = {}
) => {
  try {
    // PostgREST caps a single response at 1000 rows. With well over 1000 paid
    // orders, a plain query silently dropped the most recent ones, so newly
    // added donations never appeared here (it looked like older orders only).
    // Page through every matching order in batches (newest first) and combine.
    const ordersSelect = `
        id,
        created_at,
        donation_amount,
        payment_status,
        branch_id,
        donor:donar_id (
          id,
          simple_id,
          first_name,
          last_name,
          primary_email
        ),
        order_details (
          id,
          amount,
          billing_condition,
          package_id,
          donation_type:donation_type_id (
            donation_type_name
          ),
          package:package_id (
            package_title
          ),
          sponsorships:sponsorship!order_detail_id (
            allocated_amount,
            status
          )
        )
      `;

    const BATCH_SIZE = 1000;
    const allOrders = [];
    let from = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let query = supabase
        .from('orders')
        .select(ordersSelect)
        .eq('organisation_id', organisationId)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false })
        .range(from, from + BATCH_SIZE - 1);

      // Filter by branch if provided (org admins viewing specific branch)
      if (branchId) {
        query = query.eq('branch_id', branchId);
      }

      const { data: batch, error } = await query;
      if (error) return handleSupabaseError(error);
      if (!batch || batch.length === 0) break;

      allOrders.push(...batch);
      if (batch.length < BATCH_SIZE) break;
      from += BATCH_SIZE;
    }

    // Flatten structure and filter to sponsorship items with remaining amount
    const result = [];
    allOrders.forEach(order => {
      if (!order.donor || !order.order_details) return;

      order.order_details.forEach(detail => {
        const packageTitle = detail.package?.package_title || '';
        // Default behaviour (used by the dashboard donation-review screen):
        // only sponsorship-package line items with remaining balance.
        // includeAll=true (used by the sponsorship form) returns every paid
        // line item so users can browse the full list with their own search.
        if (
          !includeAll &&
          !packageTitle.toLowerCase().includes('sponsorship')
        ) {
          return;
        }

        // Derive the allocated/remaining amounts from the live ACTIVE
        // sponsorships attached to this donation rather than trusting the
        // stored allocated_amount/remaining_amount columns. Those columns
        // were left stale for donations sponsored via legacy/import paths
        // (and are not restored when a sponsorship is ended), which made
        // fully-allocated donations wrongly appear as "free" here. Deriving
        // is self-healing and also frees the amount again when a sponsorship
        // ends.
        const activeAllocated = (detail.sponsorships || [])
          .filter(sp => sp.status === 'active')
          .reduce((sum, sp) => sum + (Number(sp.allocated_amount) || 0), 0);
        const remainingAmount =
          Math.round(((Number(detail.amount) || 0) - activeAllocated) * 100) /
          100;
        if (!includeAll && remainingAmount <= 0) return;

        result.push({
          orderDetailId: detail.id,
          amount: detail.amount,
          allocatedAmount: activeAllocated,
          remainingAmount: remainingAmount,
          billingCondition: detail.billing_condition,
          donationType: detail.donation_type?.donation_type_name,
          sponsorshipType: packageTitle,
          donor: order.donor,
          orderId: order.id,
          createdAt: order.created_at
        });
      });
    });

    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           GUARDIANS                                        */
/* -------------------------------------------------------------------------- */

// Create guardian
export const createGuardian = async guardianData => {
  try {
    const { data, error } = await supabase
      .from('guardian')
      .insert([guardianData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           SCHOOLS                                          */
/* -------------------------------------------------------------------------- */

// Get all schools
export const getSchools = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('schools')
      .select('*, branch:branch_id(name), school_students(count)')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Transform data to include school_students_aggregate format
    const transformedData = data?.map(school => ({
      ...school,
      school_students_aggregate: {
        aggregate: {
          count: school.school_students?.[0]?.count || 0
        }
      }
    }));

    return { data: transformedData, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single school
export const getSchool = async id => {
  try {
    const { data, error } = await supabase
      .from('schools')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create school
export const createSchool = async schoolData => {
  try {
    const { data, error } = await supabase
      .from('schools')
      .insert([schoolData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update school
export const updateSchool = async (id, schoolData) => {
  try {
    const { data, error } = await supabase
      .from('schools')
      .update(schoolData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete school
export const deleteSchool = async id => {
  try {
    const { data, error } = await supabase
      .from('schools')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single school student by ID
export const getSchoolStudent = async id => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .select(
        `
        *,
        school:school_id (*)
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get school students for a beneficiary
export const getSchoolStudents = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .select(
        `
        *,
        school:school_id (*)
      `
      )
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get orphans/students in a school (by school_id)
export const getSchoolOrphans = async schoolId => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .select(
        `
        *,
        orphan:orphan_id (*),
        school:school_id (*)
      `
      )
      .eq('school_id', schoolId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create school student
export const createSchoolStudent = async studentData => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .insert([studentData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update school student
export const updateSchoolStudent = async (id, studentData) => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .update(studentData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete school student
export const deleteSchoolStudent = async id => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get school reports
export const getSchoolReports = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('school_reports')
      .select('*, user:users(id, first_name, last_name)')
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create school report
export const createSchoolReport = async reportData => {
  try {
    const { data, error } = await supabase
      .from('school_reports')
      .insert([reportData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update school report
export const updateSchoolReport = async (id, reportData) => {
  try {
    const { data, error } = await supabase
      .from('school_reports')
      .update(reportData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete school report
export const deleteSchoolReport = async id => {
  try {
    const { data, error } = await supabase
      .from('school_reports')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           HOSPITALS                                        */
/* -------------------------------------------------------------------------- */

// Get all hospitals
export const getHospitals = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('hospitals')
      .select('*, branch:branch_id(name), hospital_orphan(count)')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Transform data to include orphans_aggregate format
    const transformedData = data?.map(hospital => ({
      ...hospital,
      orphans_aggregate: {
        aggregate: {
          count: hospital.hospital_orphan?.[0]?.count || 0
        }
      }
    }));

    return { data: transformedData, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single hospital
export const getHospital = async id => {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create hospital
export const createHospital = async hospitalData => {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .insert([hospitalData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update hospital
export const updateHospital = async (id, hospitalData) => {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .update(hospitalData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete hospital
export const deleteHospital = async id => {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           MOSQUES                                          */
/* -------------------------------------------------------------------------- */

// Get all mosques
export const getMosques = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('mosques')
      .select('*, branch:branch_id(name), mosque_orphan(count)')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Transform data to include orphans_aggregate format
    const transformedData = data?.map(mosque => ({
      ...mosque,
      orphans_aggregate: {
        aggregate: {
          count: mosque.mosque_orphan?.[0]?.count || 0
        }
      }
    }));

    return { data: transformedData, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single mosque
export const getMosque = async id => {
  try {
    const { data, error } = await supabase
      .from('mosques')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create mosque
export const createMosque = async mosqueData => {
  try {
    const { data, error } = await supabase
      .from('mosques')
      .insert([mosqueData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update mosque
export const updateMosque = async (id, mosqueData) => {
  try {
    const { data, error } = await supabase
      .from('mosques')
      .update(mosqueData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete mosque
export const deleteMosque = async id => {
  try {
    const { data, error } = await supabase
      .from('mosques')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           ORPHANAGES                                       */
/* -------------------------------------------------------------------------- */

// Get all orphanages
export const getOrphanages = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('orphanages')
      .select('*, branch:branch_id(name), orphan_orphanage(count)')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Transform data to include orphans_aggregate format
    const transformedData = data?.map(orphanage => ({
      ...orphanage,
      orphans_aggregate: {
        aggregate: {
          count: orphanage.orphan_orphanage?.[0]?.count || 0
        }
      }
    }));

    return { data: transformedData, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single orphanage
export const getOrphanage = async id => {
  try {
    const { data, error } = await supabase
      .from('orphanages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create orphanage
export const createOrphanage = async orphanageData => {
  try {
    const { data, error } = await supabase
      .from('orphanages')
      .insert([orphanageData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update orphanage
export const updateOrphanage = async (id, orphanageData) => {
  try {
    const { data, error } = await supabase
      .from('orphanages')
      .update(orphanageData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete orphanage
export const deleteOrphanage = async id => {
  try {
    const { data, error } = await supabase
      .from('orphanages')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           PACKAGES                                         */
/* -------------------------------------------------------------------------- */

// Get single package by ID
export const getPackage = async id => {
  try {
    const { data, error } = await supabase
      .from('packages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create package
export const createPackage = async packageData => {
  try {
    const { data, error } = await supabase
      .from('packages')
      .insert([packageData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update package
export const updatePackage = async (id, packageData) => {
  try {
    const { data, error } = await supabase
      .from('packages')
      .update(packageData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete package
export const deletePackage = async id => {
  try {
    const { data, error } = await supabase
      .from('packages')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         URGENT APPEALS                                     */
/* -------------------------------------------------------------------------- */

// Get all urgent appeals
export const getUrgentAppeals = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('urgent_appeals')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('display_order', { ascending: true });

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single urgent appeal
export const getUrgentAppeal = async id => {
  try {
    const { data, error } = await supabase
      .from('urgent_appeals')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create urgent appeal
export const createUrgentAppeal = async appealData => {
  try {
    const { data, error } = await supabase
      .from('urgent_appeals')
      .insert([appealData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update urgent appeal
export const updateUrgentAppeal = async (id, appealData) => {
  try {
    const { data, error } = await supabase
      .from('urgent_appeals')
      .update(appealData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete urgent appeal
export const deleteUrgentAppeal = async id => {
  try {
    const { data, error } = await supabase
      .from('urgent_appeals')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update urgent appeal display order
export const updateUrgentAppealOrder = async updates => {
  try {
    for (const { id, display_order } of updates) {
      const { error } = await supabase
        .from('urgent_appeals')
        .update({ display_order })
        .eq('id', id);
      if (error) return handleSupabaseError(error);
    }
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           GIFT AID                                         */
/* -------------------------------------------------------------------------- */

// Get all gift aids
export const getGiftAids = async (organisationId, branchId, filters = {}) => {
  try {
    let query = supabase
      .from('gift_aids')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name
        ),
        order:order_id (
          id,
          simple_id,
          donation_amount
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single gift aid
export const getGiftAid = async id => {
  try {
    const { data, error } = await supabase
      .from('gift_aids')
      .select(
        `
        *,
        donar:donar_id (*),
        order:order_id (*)
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aids with donar address for unclaimed transactions
export const getGiftAidsWithAddress = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    let query = supabase
      .from('gift_aids')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          title,
          address
        ),
        order:order_id (
          donation_amount
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aid aggregate sum
export const getGiftAidAggregateSum = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    let query = supabase
      .from('gift_aids')
      .select('amount')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    const sum = data?.reduce((acc, item) => acc + (item.amount || 0), 0) || 0;
    return { data: { sum }, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get all gift aid applications
export const getGiftAidApplications = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('gift_aid_applications')
      .select(
        `
        *,
        gift_aids:gift_aids(
          id,
          amount,
          order:order_id (
            donation_amount
          )
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aid application aggregates
export const getGiftAidApplicationAggregates = async (
  organisationId,
  branchId
) => {
  try {
    let query = supabase
      .from('gift_aid_applications')
      .select('status')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Count by status
    const completedCount =
      data?.filter(app => app.status === 'completed').length || 0;
    const submittedCount =
      data?.filter(app => app.status === 'submitted').length || 0;
    const awaitingCount =
      data?.filter(app => app.status === 'awaiting').length || 0;

    return {
      data: {
        completedCount,
        submittedCount,
        awaitingCount
      },
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aids by application ID
export const getGiftAidsByApplicationId = async applicationId => {
  try {
    const { data, error } = await supabase
      .from('gift_aids')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          title,
          address
        ),
        order:order_id (
          donation_amount
        )
      `
      )
      .eq('gift_aid_application_id', applicationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aid aggregate by application ID
export const getGiftAidAggregateByApplicationId = async applicationId => {
  try {
    const { data, error } = await supabase
      .from('gift_aids')
      .select('amount, status, order:order_id (donation_amount)')
      .eq('gift_aid_application_id', applicationId);

    if (error) return handleSupabaseError(error);

    const sum = data?.reduce((acc, item) => acc + (item.amount || 0), 0) || 0;
    const count = data?.length || 0;
    const acceptedCount =
      data?.filter(item => item.status === 'accepted').length || 0;
    const rejectedCount =
      data?.filter(item => item.status === 'rejected').length || 0;

    return {
      data: {
        sum,
        count,
        acceptedCount,
        rejectedCount,
        nodes: data
      },
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create gift aid application
export const createGiftAidApplication = async applicationData => {
  try {
    const { data, error } = await supabase
      .from('gift_aid_applications')
      .insert(applicationData)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update gift aids status
export const updateGiftAidsStatus = async (
  giftAidIds,
  status,
  applicationId
) => {
  try {
    const updateData = {
      status,
      gift_aid_application_id: applicationId
    };

    const { data, error } = await supabase
      .from('gift_aids')
      .update(updateData)
      .in('id', giftAidIds)
      .select();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update gift aid application status
export const updateGiftAidApplicationStatus = async (
  id,
  status,
  submittedDate
) => {
  try {
    const updateData = {
      status,
      ...(submittedDate && { submitted_date: submittedDate })
    };

    const { data, error } = await supabase
      .from('gift_aid_applications')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update single gift aid status
export const updateGiftAidStatus = async (id, status) => {
  try {
    const { data, error } = await supabase
      .from('gift_aids')
      .update({ status })
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    INSTITUTION-BENEFICIARY LINKS                           */
/* -------------------------------------------------------------------------- */

// Get hospital orphans (beneficiaries linked to hospitals)
export const getHospitalOrphans = async hospitalId => {
  try {
    const { data, error } = await supabase
      .from('hospital_orphan')
      .select(
        `
        *,
        orphan:orphan_id (*),
        hospital:hospital_id (*)
      `
      )
      .eq('hospital_id', hospitalId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create hospital orphan link
export const createHospitalOrphan = async data => {
  try {
    const { data: result, error } = await supabase
      .from('hospital_orphan')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete hospital orphan link
export const deleteHospitalOrphan = async id => {
  try {
    const { error } = await supabase
      .from('hospital_orphan')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get hospitals for a beneficiary (by orphan_id)
export const getBeneficiaryHospitals = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('hospital_orphan')
      .select(
        `
        *,
        hospital:hospital_id (*)
      `
      )
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get mosques for a beneficiary (by orphan_id)
export const getBeneficiaryMosques = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('mosque_orphan')
      .select(
        `
        *,
        mosque:mosque_id (*)
      `
      )
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get orphanages for a beneficiary (by orphan_id)
export const getBeneficiaryOrphanages = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('orphan_orphanage')
      .select(
        `
        *,
        orphanage:orphanage_id (*)
      `
      )
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get mosque orphans
export const getMosqueOrphans = async mosqueId => {
  try {
    const { data, error } = await supabase
      .from('mosque_orphan')
      .select(
        `
        *,
        orphan:orphan_id (*),
        mosque:mosque_id (*)
      `
      )
      .eq('mosque_id', mosqueId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create mosque orphan link
export const createMosqueOrphan = async data => {
  try {
    const { data: result, error } = await supabase
      .from('mosque_orphan')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete mosque orphan link
export const deleteMosqueOrphan = async id => {
  try {
    const { error } = await supabase
      .from('mosque_orphan')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get orphanage orphans
export const getOrphanageOrphans = async orphanageId => {
  try {
    const { data, error } = await supabase
      .from('orphan_orphanage')
      .select(
        `
        *,
        orphan:orphan_id (*),
        orphanage:orphanage_id (*)
      `
      )
      .eq('orphanage_id', orphanageId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create orphanage orphan link
export const createOrphanageOrphan = async data => {
  try {
    const { data: result, error } = await supabase
      .from('orphan_orphanage')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete orphanage orphan link
export const deleteOrphanageOrphan = async id => {
  try {
    const { error } = await supabase
      .from('orphan_orphanage')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    MEDICAL REPORTS & GALLERY                               */
/* -------------------------------------------------------------------------- */

// Get medical reports for beneficiary
export const getMedicalReports = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('medical_reports')
      .select('*')
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create medical report
export const createMedicalReport = async reportData => {
  try {
    const { data, error } = await supabase
      .from('medical_reports')
      .insert([reportData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    MOSQUE REPORTS                                          */
/* -------------------------------------------------------------------------- */

// Get mosque reports for beneficiary
export const getMosqueReports = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('mosque_reports')
      .select('*, user:users(id, first_name, last_name)')
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create mosque report
export const createMosqueReport = async reportData => {
  try {
    const { data, error } = await supabase
      .from('mosque_reports')
      .insert([reportData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update mosque report
export const updateMosqueReport = async (id, reportData) => {
  try {
    const { data, error } = await supabase
      .from('mosque_reports')
      .update(reportData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete mosque report
export const deleteMosqueReport = async id => {
  try {
    const { error } = await supabase
      .from('mosque_reports')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    ORPHANAGE REPORTS                                       */
/* -------------------------------------------------------------------------- */

// Get orphanage reports for beneficiary
export const getOrphanageReports = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('orphanage_reports')
      .select('*, user:users(id, first_name, last_name)')
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create orphanage report
export const createOrphanageReport = async reportData => {
  try {
    const { data, error } = await supabase
      .from('orphanage_reports')
      .insert([reportData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update orphanage report
export const updateOrphanageReport = async (id, reportData) => {
  try {
    const { data, error } = await supabase
      .from('orphanage_reports')
      .update(reportData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete orphanage report
export const deleteOrphanageReport = async id => {
  try {
    const { error } = await supabase
      .from('orphanage_reports')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gallery items for beneficiary
export const getGalleryItems = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('gallery')
      .select(
        `
        *,
        orphan:orphan_id (
          id,
          first_name,
          last_name
        )
      `
      )
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create gallery item
export const createGalleryItem = async itemData => {
  try {
    const { data, error } = await supabase
      .from('gallery')
      .insert([itemData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP SETTINGS                                    */
/* -------------------------------------------------------------------------- */

// Get sponsorship types
export const getSponsorshipTypes = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_type')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('type');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship conditions
export const getSponsorshipConditions = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_condition')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('condition');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship costs
export const getSponsorshipCosts = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_cost')
      .select('*, sponsorship_type:sponsorship_type_id(id, type)')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('amount');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship priorities
export const getSponsorshipPriorities = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_priority')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('priority_type');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship durations
export const getSponsorshipDurations = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_duration')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('duration');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship statuses
export const getSponsorshipStatuses = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_status')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('status');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create sponsorship priority
export const createSponsorshipPriority = async priorityData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_priority')
      .insert([priorityData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update sponsorship priority
export const updateSponsorshipPriority = async (id, priorityData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_priority')
      .update(priorityData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete sponsorship priority
export const deleteSponsorshipPriority = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_priority')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create sponsorship status
export const createSponsorshipStatus = async statusData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_status')
      .insert([statusData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update sponsorship status
export const updateSponsorshipStatus = async (id, statusData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_status')
      .update(statusData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete sponsorship status
export const deleteSponsorshipStatus = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_status')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// CRUD for Sponsorship Types
export const createSponsorshipType = async typeData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_type')
      .insert([typeData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateSponsorshipType = async (id, typeData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_type')
      .update(typeData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteSponsorshipType = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_type')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// CRUD for Sponsorship Conditions
export const createSponsorshipCondition = async conditionData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_condition')
      .insert([conditionData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateSponsorshipCondition = async (id, conditionData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_condition')
      .update(conditionData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteSponsorshipCondition = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_condition')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// CRUD for Sponsorship Costs
export const createSponsorshipCost = async costData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_cost')
      .insert([costData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateSponsorshipCost = async (id, costData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_cost')
      .update(costData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteSponsorshipCost = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_cost')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// CRUD for Sponsorship Durations
export const createSponsorshipDuration = async durationData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_duration')
      .insert([durationData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateSponsorshipDuration = async (id, durationData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_duration')
      .update(durationData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteSponsorshipDuration = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_duration')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Contact table (for institutions)
export const getContacts = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('contact')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                        GUARDIAN & LOCATION OPTIONS                         */
/* -------------------------------------------------------------------------- */

// Get guardian father status options
export const getGuardianFatherStatus = async () => {
  try {
    const { data, error } = await supabase
      .from('guardian_father_status')
      .select('*')
      .order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get guardian mother status options
export const getGuardianMotherStatus = async () => {
  try {
    const { data, error } = await supabase
      .from('guardian_mother_status')
      .select('*')
      .order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get guardian relationship options
export const getGuardianRelationship = async () => {
  try {
    const { data, error } = await supabase
      .from('guardian_relationShip')
      .select('*')
      .order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get location condition options
export const getLocationConditions = async () => {
  try {
    const { data, error } = await supabase
      .from('location_condition')
      .select('*')
      .order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get shortcuts
export const getShortcuts = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('shortcuts')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create shortcut
export const createShortcut = async shortcutData => {
  try {
    const { data, error } = await supabase
      .from('shortcuts')
      .insert([shortcutData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR TASKS                                      */
/* -------------------------------------------------------------------------- */

// Get donor tasks
export const getDonorTasks = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donor_tasks')
      .select('*')
      .eq('donor_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor task
export const createDonorTask = async taskData => {
  try {
    const { data, error } = await supabase
      .from('donor_tasks')
      .insert([taskData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor task
export const updateDonorTask = async (id, taskData) => {
  try {
    const { data, error } = await supabase
      .from('donor_tasks')
      .update(taskData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor task
export const deleteDonorTask = async id => {
  try {
    const { error } = await supabase.from('donor_tasks').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data: { id }, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR COMMENTS                                   */
/* -------------------------------------------------------------------------- */

// Get donor comments
export const getDonorComments = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donor_comments')
      .select(
        `
        *,
        user:users!reporter_id(first_name, last_name)
      `
      )
      .eq('donor_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor comment
export const createDonorComment = async commentData => {
  try {
    const { data, error } = await supabase
      .from('donor_comments')
      .insert([commentData])
      .select(
        `
        *,
        user:users!reporter_id(first_name, last_name)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor comment
export const updateDonorComment = async (id, commentData) => {
  try {
    const { data, error } = await supabase
      .from('donor_comments')
      .update(commentData)
      .eq('id', id)
      .select(
        `
        *,
        user:users!reporter_id(first_name, last_name)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor comments (multiple)
export const deleteDonorComments = async ids => {
  try {
    const { error } = await supabase
      .from('donor_comments')
      .delete()
      .in('id', ids);

    if (error) return handleSupabaseError(error);
    return { data: { ids }, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           STAFF (USERS)                                    */
/* -------------------------------------------------------------------------- */

// Get branch staff
export const getBranchStaff = async branchId => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        *,
        user:users!user_id(id, first_name, last_name, email)
      `
      )
      .eq('branch_id', branchId)
      .eq('is_active', true);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor assigned staff
export const updateDonorAssignedStaff = async (donorId, staffId) => {
  try {
    const { data, error } = await supabase
      .from('donars')
      .update({ user_id: staffId })
      .eq('id', donorId)
      .select(
        `
        *,
        user:users!user_id(id, first_name, last_name)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get top donations for donor
export const getTopDonations = async (donorId, limit = 3) => {
  try {
    const { data, error } = await supabase
      .from('order_details')
      .select(
        `
        id,
        amount,
        package:packages!inner(package_title)
      `
      )
      .eq('order.donar_id', donorId)
      .order('amount', { ascending: false })
      .limit(limit);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              LOGS                                          */
/* -------------------------------------------------------------------------- */

// Get weraise module by name
export const getWeraiseModule = async moduleName => {
  try {
    const { data, error } = await supabase
      .from('weraise_module')
      .select('*')
      .eq('name', moduleName)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get logs for a module
export const getLogs = async (organisationId, moduleId, moduleReferenceId) => {
  try {
    const { data, error } = await supabase
      .from('all_logs')
      .select('*')
      .eq('organisation_id', organisationId)
      .eq('weraise_module_id', moduleId)
      .eq('module_reference_id', moduleReferenceId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Insert log
export const insertLog = async logData => {
  try {
    const { data, error } = await supabase
      .from('all_logs')
      .insert([logData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              CALENDAR EVENTS                               */
/* -------------------------------------------------------------------------- */

// Get calendar events
export const getCalendarEvents = async (organisationId, branchId) => {
  try {
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('organisation_id', organisationId)
      .eq('branch_id', branchId)
      .order('start_date', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Insert calendar event
export const insertCalendarEvent = async eventData => {
  try {
    const { data, error } = await supabase
      .from('calendar_events')
      .insert([eventData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update calendar event
export const updateCalendarEvent = async (id, eventData) => {
  try {
    const { data, error } = await supabase
      .from('calendar_events')
      .update(eventData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete calendar event
export const deleteCalendarEvent = async id => {
  try {
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    DONATION SETTINGS MUTATIONS                             */
/* -------------------------------------------------------------------------- */

// Donation Type CRUD
export const createDonationType = async data => {
  try {
    const { data: result, error } = await supabase
      .from('donation_type')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateDonationType = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('donation_type')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteDonationType = async id => {
  try {
    const { error } = await supabase
      .from('donation_type')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Location CRUD
export const createLocation = async data => {
  try {
    const { data: result, error } = await supabase
      .from('location')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateLocation = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('location')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteLocation = async id => {
  try {
    const { error } = await supabase.from('location').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Campaign CRUD
export const createCampaign = async data => {
  try {
    const { data: result, error } = await supabase
      .from('campaign')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateCampaign = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('campaign')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteCampaign = async id => {
  try {
    const { error } = await supabase.from('campaign').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Transaction Fee CRUD
export const createTransactionFee = async data => {
  try {
    const { data: result, error } = await supabase
      .from('transaction_fee')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateTransactionFee = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('transaction_fee')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteTransactionFee = async id => {
  try {
    const { error } = await supabase
      .from('transaction_fee')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Donation Amount CRUD
export const createDonationAmount = async data => {
  try {
    const { data: result, error } = await supabase
      .from('donation_amount')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateDonationAmount = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('donation_amount')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteDonationAmount = async id => {
  try {
    const { error } = await supabase
      .from('donation_amount')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Package Category CRUD
export const createPackageCategory = async data => {
  try {
    const { data: result, error } = await supabase
      .from('package_category')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updatePackageCategory = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('package_category')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deletePackageCategory = async id => {
  try {
    const { error } = await supabase
      .from('package_category')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Donation Currency CRUD
export const createDonationCurrency = async data => {
  try {
    const { data: result, error } = await supabase
      .from('donation_currency')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateDonationCurrency = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('donation_currency')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteDonationCurrency = async id => {
  try {
    const { error } = await supabase
      .from('donation_currency')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         GENERIC CRUD HELPERS                               */
/* -------------------------------------------------------------------------- */

// Generic get all for any table
export const getAll = async (tableName, organisationId) => {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .select('id, name')
      .eq('organisation_id', organisationId)
      .order('name', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Generic create for any table with name field
export const createOption = async (tableName, data) => {
  try {
    const { data: result, error } = await supabase
      .from(tableName)
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Generic update for any table
export const updateOption = async (tableName, id, data) => {
  try {
    const { data: result, error } = await supabase
      .from(tableName)
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Generic delete for any table
export const deleteOption = async (tableName, id) => {
  try {
    const { error } = await supabase.from(tableName).delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get contact by mosque ID
export const getContactByMosqueId = async mosqueId => {
  try {
    const { data, error } = await supabase
      .from('contact')
      .select('*')
      .eq('mosque_id', mosqueId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get contact by orphanage ID
export const getContactByOrphanageId = async orphanageId => {
  try {
    const { data, error } = await supabase
      .from('contact')
      .select('*')
      .eq('orphanage_id', orphanageId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// ==========================================
// Beneficiary Status Logs
// ==========================================

// Insert a beneficiary status log entry
export const insertBeneficiaryStatusLog = async logData => {
  try {
    const { data, error } = await supabase
      .from('beneficiary_status_logs')
      .insert([logData])
      .select()
      .single();

    if (error) {
      console.error('Error inserting status log:', error);
      return handleSupabaseError(error);
    }
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get beneficiary status logs
export const getBeneficiaryStatusLogs = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('beneficiary_status_logs')
      .select('*')
      .eq('beneficiary_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              REMINDERS                                      */
/* -------------------------------------------------------------------------- */

// Get reminders with optional filters
export const getReminders = async (organisationId, branchId, filters = {}) => {
  try {
    let query = supabase
      .from('reminders')
      .select(
        `
        *,
        assigned_to:users!assigned_to_user_id(id, first_name, last_name),
        created_by:users!created_by_user_id(id, first_name, last_name)
      `
      )
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    if (filters.assignedToUserId) {
      query = query.eq('assigned_to_user_id', filters.assignedToUserId);
    }

    if (filters.reminderType) {
      query = query.eq('reminder_type', filters.reminderType);
    }

    // Date range filters for calendar view
    if (filters.startDate) {
      query = query.gte('due_date', filters.startDate);
    }

    if (filters.endDate) {
      query = query.lte('due_date', filters.endDate);
    }

    // Filter out completed reminders unless explicitly included
    if (filters.includeCompleted === false) {
      query = query.neq('status', 'completed');
    }

    const { data, error } = await query.order('due_date', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data: await attachReminderEntities(data || []), error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get my reminders (assigned to current user)
// Uses RPC function to bypass RLS and get creator/assignee names
// Function uses auth.uid() internally for security - no user-supplied IDs
export const getMyReminders = async () => {
  try {
    const { data, error } = await supabase.rpc('get_my_reminders_with_users');

    if (error) return handleSupabaseError(error);

    // Transform the flat response to match the expected nested structure
    const transformedData = data?.map(reminder => ({
      ...reminder,
      assigned_to: reminder.assigned_to_first_name
        ? {
            id: reminder.assigned_to_user_id,
            first_name: reminder.assigned_to_first_name,
            last_name: reminder.assigned_to_last_name
          }
        : null,
      created_by: reminder.created_by_first_name
        ? {
            id: reminder.created_by_user_id,
            first_name: reminder.created_by_first_name,
            last_name: reminder.created_by_last_name
          }
        : null
    }));

    return { data: transformedData, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create reminder
export const createReminder = async reminderData => {
  try {
    const { data, error } = await supabase
      .from('reminders')
      .insert([reminderData])
      .select(
        `
        *,
        assigned_to:users!assigned_to_user_id(id, first_name, last_name),
        created_by:users!created_by_user_id(id, first_name, last_name)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update reminder
export const updateReminder = async (id, reminderData) => {
  try {
    const { data, error } = await supabase
      .from('reminders')
      .update(reminderData)
      .eq('id', id)
      .select(
        `
        *,
        assigned_to:users!assigned_to_user_id(id, first_name, last_name),
        created_by:users!created_by_user_id(id, first_name, last_name)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Resolve the donor / beneficiary a reminder is linked to (#28). Done here
// because entity_id is polymorphic (no FK), so PostgREST can't embed it.
export const attachReminderEntities = async rows => {
  const donorIds = [
    ...new Set(
      rows
        .filter(r => r.entity_type === 'donor' && r.entity_id)
        .map(r => r.entity_id)
    )
  ];
  const orphanIds = [
    ...new Set(
      rows
        .filter(r => r.entity_type === 'beneficiary' && r.entity_id)
        .map(r => r.entity_id)
    )
  ];
  const [donors, orphans] = await Promise.all([
    donorIds.length
      ? selectInChunks(
          () => supabase.from('donars').select('id, first_name, last_name'),
          'id',
          donorIds
        )
      : { data: [] },
    orphanIds.length
      ? selectInChunks(
          () =>
            supabase
              .from('orphans')
              .select('id, first_name, last_name, simple_id'),
          'id',
          orphanIds
        )
      : { data: [] }
  ]);
  const names = new Map();
  (donors.data || []).forEach(d =>
    names.set(d.id, `${d.first_name || ''} ${d.last_name || ''}`.trim())
  );
  (orphans.data || []).forEach(o =>
    names.set(
      o.id,
      `${o.first_name || ''} ${o.last_name || ''}`.trim() +
        (o.simple_id ? ` (B${o.simple_id})` : '')
    )
  );
  return rows.map(r => ({
    ...r,
    entity_name: r.entity_id ? names.get(r.entity_id) || null : null
  }));
};

// Reminders linked to one donor or beneficiary (Notes tab card)
export const getRemindersForEntity = async (entityType, entityId) => {
  try {
    const { data, error } = await supabase
      .from('reminders')
      .select(
        '*, assigned_to:users!assigned_to_user_id(id, first_name, last_name), created_by:users!created_by_user_id(id, first_name, last_name)'
      )
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('status', { ascending: false })
      .order('due_date', { ascending: true });
    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Reopen a completed reminder, optionally handing it to someone else (#32)
export const reopenReminder = async (id, { note, assignedToUserId } = {}) => {
  try {
    const updateData = {
      status: 'pending',
      completed_at: null,
      completed_by: null,
      overdue_notified_at: null,
      reopened_at: new Date().toISOString(),
      reopen_note: note || null
    };
    if (assignedToUserId) updateData.assigned_to_user_id = assignedToUserId;
    const { data, error } = await supabase
      .from('reminders')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();
    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Complete reminder
export const completeReminder = async (id, completedBy = null) => {
  try {
    const updateData = {
      status: 'completed',
      completed_at: new Date().toISOString(),
      completed_by: completedBy
    };
    const { data, error } = await supabase
      .from('reminders')
      .update(updateData)
      .eq('id', id)
      .select(
        `
        *,
        assigned_to:users!assigned_to_user_id(id, first_name, last_name, email),
        created_by:users!created_by_user_id(id, first_name, last_name, email)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete reminder
export const deleteReminder = async id => {
  try {
    const { error } = await supabase.from('reminders').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data: { id }, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         DASHBOARD STATS                                     */
/* -------------------------------------------------------------------------- */

// Get active sponsorships count
export const getActiveSponsorshipsCount = async (organisationId, branchId) => {
  try {
    // Sponsorship doesn't have organisation_id/branch_id directly - filter through orphan relation
    let query = supabase
      .from('sponsorship')
      .select('id, orphan:orphan_id!inner(organisation_id, branch_id)', {
        count: 'exact',
        head: true
      })
      .eq('status', 'active')
      .eq('orphan.organisation_id', organisationId);

    if (branchId) {
      query = query.eq('orphan.branch_id', branchId);
    }

    const { count, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data: count, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get monthly donation amount (current month)
export const getMonthlyDonationAmount = async (organisationId, branchId) => {
  try {
    // Get start and end of current month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59
    );

    let query = supabase
      .from('orders')
      .select('donation_amount')
      .eq('organisation_id', organisationId)
      .eq('payment_status', 'paid')
      .gte('created_at', startOfMonth.toISOString())
      .lte('created_at', endOfMonth.toISOString());

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Calculate sum manually
    const sum =
      data?.reduce((acc, order) => acc + (order.donation_amount || 0), 0) || 0;

    return { data: sum, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get pending reminders count for a user
export const getPendingRemindersCount = async (
  organisationId,
  branchId,
  userId
) => {
  try {
    // Count reminders where user is assigned OR created them (matches getMyReminders)
    const query = supabase
      .from('reminders')
      .select('*', { count: 'exact', head: true })
      .eq('organisation_id', organisationId)
      .or(`assigned_to_user_id.eq.${userId},created_by_user_id.eq.${userId}`)
      .eq('status', 'pending');

    const { count, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data: count, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get organisation users (for reminder assignment dropdown)
export const getOrganisationUsers = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('users_roles')
      .select(
        `
        user_id,
        user:users!user_id(id, first_name, last_name, email)
      `
      )
      .eq('organisation_id', organisationId)
      .eq('is_active', true);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Flatten and dedupe users
    const usersMap = new Map();
    data?.forEach(item => {
      if (item.user && !usersMap.has(item.user.id)) {
        usersMap.set(item.user.id, item.user);
      }
    });

    return { data: Array.from(usersMap.values()), error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get branch admins for a specific branch
export const getBranchAdmins = async (organisationId, branchId) => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        user_id,
        user:users!user_id(id, first_name, last_name, email),
        role:roles!role_id(id, name)
      `
      )
      .eq('organisation_id', organisationId)
      .eq('branch_id', branchId)
      .eq('is_active', true);

    if (error) return handleSupabaseError(error);

    // Filter to only include branch admins (or org admins)
    const admins = data?.filter(item => {
      const roleName = item.role?.name?.toLowerCase() || '';
      return roleName.includes('admin');
    });

    // Extract unique users
    const usersMap = new Map();
    admins?.forEach(item => {
      if (item.user && !usersMap.has(item.user.id)) {
        usersMap.set(item.user.id, item.user);
      }
    });

    return { data: Array.from(usersMap.values()), error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         BASKET SESSIONS (Cart Abandonment)                  */
/* -------------------------------------------------------------------------- */

// Get incomplete basket sessions (pending status)
export const getIncompleteSessions = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('basket_session')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          email,
          primary_email
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('payment_status', 'pending')
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get incomplete sessions count
export const getIncompleteSessionsCount = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('basket_session')
      .select('*', { count: 'exact', head: true })
      .eq('organisation_id', organisationId)
      .eq('payment_status', 'pending');

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { count, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data: count, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                                 PLEDGES                                    */
/* -------------------------------------------------------------------------- */

const PLEDGE_ORDER_EMBED = `
  orders:orders!pledge_id (
    id,
    simple_id,
    donation_amount,
    payment_status,
    created_at
  )`;

// Sum of money actually received against a pledge — ALWAYS derived from the
// linked paid orders, never stored on the pledge row.
export const sumPledgePaid = orders =>
  (orders || [])
    .filter(o => ['paid', 'partially_refunded'].includes(o.payment_status))
    .reduce((sum, o) => sum + (Number(o.donation_amount) || 0), 0);

// Workflow states (on hold, cancelled, awaiting follow-up) live on the row;
// money states (partially paid, fulfilled) are derived from real payments.
export const derivePledgeStatus = (pledge, paidTotal) => {
  if (['cancelled', 'on_hold'].includes(pledge.status)) return pledge.status;
  if (
    pledge.total_value &&
    paidTotal >= Number(pledge.total_value) &&
    pledge.duration_type === 'fixed'
  ) {
    return 'fulfilled';
  }
  if (paidTotal > 0) return 'partially_paid';
  return pledge.status;
};

// List pledges with donor, campaign and linked payments (for derived balances)
export const getPledges = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('pledges')
      .select(
        `
        *,
        donor:donar_id (
          id,
          simple_id,
          first_name,
          last_name,
          primary_email,
          primary_phone
        ),
        package:package_id (
          id,
          package_title
        ),
        assigned_user:assigned_to (
          id,
          first_name,
          last_name
        ),
        ${PLEDGE_ORDER_EMBED}
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;
    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Single pledge with everything the profile page needs
export const getPledge = async pledgeId => {
  try {
    const { data, error } = await supabase
      .from('pledges')
      .select(
        `
        *,
        donor:donar_id (
          id,
          simple_id,
          first_name,
          last_name,
          primary_email,
          primary_phone,
          primary_address
        ),
        package:package_id (
          id,
          package_title
        ),
        assigned_user:assigned_to (
          id,
          first_name,
          last_name
        ),
        created_by_user:created_by (
          id,
          first_name,
          last_name
        ),
        changes:pledge_changes (
          id, field, old_value, new_value, changed_at,
          changed_by_user:changed_by ( first_name, last_name )
        ),
        follow_ups:pledge_follow_ups (
          id,
          due_date,
          method,
          notes,
          outcome,
          status,
          completed_at,
          created_at,
          created_by_user:created_by (
            first_name,
            last_name
          ),
          completed_by_user:completed_by (
            first_name,
            last_name
          )
        ),
        ${PLEDGE_ORDER_EMBED}
      `
      )
      .eq('id', pledgeId)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const createPledge = async pledgeData => {
  try {
    const { data, error } = await supabase
      .from('pledges')
      .insert([pledgeData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updatePledge = async (pledgeId, pledgeData) => {
  try {
    const { data, error } = await supabase
      .from('pledges')
      .update(pledgeData)
      .eq('id', pledgeId)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const createPledgeFollowUp = async followUpData => {
  try {
    const { data, error } = await supabase
      .from('pledge_follow_ups')
      .insert([followUpData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updatePledgeFollowUp = async (followUpId, followUpData) => {
  try {
    const { data, error } = await supabase
      .from('pledge_follow_ups')
      .update(followUpData)
      .eq('id', followUpId)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Minimal staff list for the "Assigned Staff" dropdown
export const getOrganisationStaff = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name')
      .eq('organisation_id', organisationId)
      .order('first_name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              NOTIFICATIONS                                 */
/* -------------------------------------------------------------------------- */

// System-generated alerts (docs/auto-notification-alerts-plan.md). Rows are
// created by DB triggers/scheduled jobs; these RPCs resolve the user and
// organisation from auth.uid() internally, like get_my_reminders_with_users.

// completed=false returns open alerts (the bell); completed=true returns
// actioned ones with who completed them, when, and their notes.
export const getMyNotifications = async (limit = 20, completed = false) => {
  try {
    const { data, error } = await supabase.rpc('get_my_notifications', {
      p_limit: limit,
      p_completed: completed
    });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Completion is org-wide: actioning an alert clears it for every staff member,
// so the notes explain to everyone else what was done.
export const completeNotification = async (notificationId, notes) => {
  try {
    const { error } = await supabase.rpc('complete_notification', {
      p_notification_id: notificationId,
      p_notes: notes || null
    });

    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Reopening keeps the earlier completion in the audit trail rather than
// erasing it, so the history reads "completed by X, reopened by Y".
export const reopenNotification = async (notificationId, notes) => {
  try {
    const { error } = await supabase.rpc('reopen_notification', {
      p_notification_id: notificationId,
      p_notes: notes || null
    });

    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const getUnreadNotificationCount = async () => {
  try {
    const { data, error } = await supabase.rpc('get_unread_notification_count');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const markNotificationRead = async notificationId => {
  try {
    const { error } = await supabase.rpc('mark_notification_read', {
      p_notification_id: notificationId
    });

    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const markAllNotificationsRead = async () => {
  try {
    const { error } = await supabase.rpc('mark_all_notifications_read');

    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           FACILITY CLOSURE                                 */
/* -------------------------------------------------------------------------- */

// kind: 'school' | 'mosque' | 'hospital' | 'orphanage'. Ends all open
// enrolments, stamps closed_at/closed_reason and raises a facility_closed
// alert - all inside the close_facility definer function.
export const closeFacility = async (kind, facilityId, reason) => {
  try {
    const { error } = await supabase.rpc('close_facility', {
      p_kind: kind,
      p_facility_id: facilityId,
      p_reason: reason || null
    });

    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Clears the closure flag only; enrolments ended by the close stay ended.
export const reopenFacility = async (kind, facilityId) => {
  try {
    const { error } = await supabase.rpc('reopen_facility', {
      p_kind: kind,
      p_facility_id: facilityId
    });

    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         HAFIZ QURAN (JUZ) PROGRESS                         */
/* -------------------------------------------------------------------------- */

export const getJuzProgress = async orphanId => {
  try {
    const { data, error } = await supabase
      .from('hafiz_juz_progress')
      .select(
        `
        id, juz, recorded_on, notes, created_at,
        recorded_by_user:recorded_by (first_name, last_name)
      `
      )
      .eq('orphan_id', orphanId)
      .order('recorded_on', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const createJuzProgress = async progressData => {
  try {
    const { data, error } = await supabase
      .from('hafiz_juz_progress')
      .insert([progressData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteJuzProgress = async id => {
  try {
    const { error } = await supabase
      .from('hafiz_juz_progress')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Current Juz per child for the beneficiaries list filter (latest row wins)
export const getCurrentJuzByOrphan = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('hafiz_juz_progress')
      .select('orphan_id, juz, recorded_on')
      .eq('organisation_id', organisationId)
      .order('recorded_on', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    const latest = {};
    (data || []).forEach(row => {
      if (!latest[row.orphan_id]) latest[row.orphan_id] = row;
    });
    return { data: latest, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                                CASE STUDIES                                */
/* -------------------------------------------------------------------------- */

const CASE_STUDY_SELECT = `
  id, title, life_before, how_found_out, experience, life_now, quote,
  study_date, photos, created_at, updated_at, branch_id,
  orphan:orphan_id (id, simple_id, first_name, middle_name, last_name, image, country),
  branch:branch_id (name),
  created_by_user:created_by (first_name, last_name)
`;

export const getCaseStudies = async (organisationId, branchId, orphanId) => {
  try {
    let query = supabase
      .from('case_studies')
      .select(CASE_STUDY_SELECT)
      .eq('organisation_id', organisationId)
      .order('study_date', { ascending: false });
    if (branchId) query = query.eq('branch_id', branchId);
    if (orphanId) query = query.eq('orphan_id', orphanId);

    const { data, error } = await query;
    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const createCaseStudy = async caseStudyData => {
  try {
    const { data, error } = await supabase
      .from('case_studies')
      .insert([caseStudyData])
      .select(CASE_STUDY_SELECT)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateCaseStudy = async (id, caseStudyData) => {
  try {
    const { data, error } = await supabase
      .from('case_studies')
      .update(caseStudyData)
      .eq('id', id)
      .select(CASE_STUDY_SELECT)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteCaseStudy = async id => {
  try {
    const { error } = await supabase.from('case_studies').delete().eq('id', id);
    if (error) return handleSupabaseError(error);
    return { data: true, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           MEDICAL FORMS (volunteers)                        */
/* -------------------------------------------------------------------------- */

export const getMedicalForms = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('medical_forms')
      .select('*, reviewed_by_user:reviewed_by (first_name, last_name)')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const reviewMedicalForm = async (id, { status, adminNotes, userId }) => {
  try {
    const { data, error } = await supabase
      .from('medical_forms')
      .update({
        status,
        admin_notes: adminNotes ?? null,
        reviewed_by: status === 'reviewed' ? userId : null,
        reviewed_at: status === 'reviewed' ? new Date().toISOString() : null
      })
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};
